import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Save, X, Type, Square,
  MousePointer, Loader2, FolderOpen, Trash2, RotateCcw, Redo2,
  Pipette, Circle, Minus, ArrowRight, Pencil, Printer, Wand2, Edit3, Sparkles, Eraser
} from "lucide-react";

const CanvasPDFEditor = ({ document, onClose, onSave, onSwitchToTextEditor }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageImage, setPageImage] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [activeTool, setActiveTool] = useState("select");
  
  // Canvas elements for current page
  const [rectangles, setRectangles] = useState({});
  const [textBoxes, setTextBoxes] = useState({});
  const [circles, setCircles] = useState({});
  const [lines, setLines] = useState({});
  const [arrows, setArrows] = useState({});
  const [freehand, setFreehand] = useState({});
  
  // History for Undo/Redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isUndoRedo, setIsUndoRedo] = useState(false);
  
  // Currently selected/editing element
  const [selectedElement, setSelectedElement] = useState(null);
  const [editingTextBox, setEditingTextBox] = useState(null);
  
  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [currentShape, setCurrentShape] = useState(null);
  const [currentFreehandPoints, setCurrentFreehandPoints] = useState([]);
  
  // Colors
  const [fillColor, setFillColor] = useState("#FFFFFF");
  const [strokeColor, setStrokeColor] = useState("#FF0000");
  const [textColor, setTextColor] = useState("#000000");
  const [fontSize, setFontSize] = useState(14);
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [sampledPageColor, setSampledPageColor] = useState(null); // Detected page background color
  const [usePageColorMode, setUsePageColorMode] = useState(false); // When true, use sampled color for fills

  // Eraser brush state — works on mouse + touch via pointer events
  const [eraserBrushSize, setEraserBrushSize] = useState(28);
  const [smartEraser, setSmartEraser] = useState(false); // when on, tap-to-erase-word using OCR/vector bbox
  const [hoveredTextBbox, setHoveredTextBbox] = useState(null); // {x,y,width,height,text} from /text-bbox-at-point
  const eraserStateRef = useRef(null); // { active: bool, lastX, lastY, color }
  const [eraserPreview, setEraserPreview] = useState(null); // { x, y } for hover/active indicator
  const smartLookupRef = useRef({ timer: null, lastReq: 0 });
  
  // PDF text block inline editing
  const [pdfTextEdits, setPdfTextEdits] = useState({}); // {page: {blockId: newText}}
  const [editingPdfBlock, setEditingPdfBlock] = useState(null);
  const [editPdfText, setEditPdfText] = useState('');
  const editInputRef = useRef(null);

  // Side-by-side compare modal
  const [compareOpen, setCompareOpen] = useState(false);
  const [comparePreviewUrl, setComparePreviewUrl] = useState(null);
  const [comparing, setComparing] = useState(false);

  // Dialogs
  const [addTextDialog, setAddTextDialog] = useState(false);
  const [newTextPosition, setNewTextPosition] = useState({ x: 0, y: 0 });
  const [newText, setNewText] = useState("");
  const [saveToLibraryDialog, setSaveToLibraryDialog] = useState(false);
  
  // Dragging state
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Resizing state
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null); // 'nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'
  const [resizeStart, setResizeStart] = useState(null);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  // Ref for text-box drag gesture (pointer events, works on mouse + touch)
  const textDragRef = useRef(null);

  // Map PyMuPDF / Liberation font keys → best CSS font stack for on-screen preview
  // so the displayed text closely matches what the PDF will be rendered with.
  const cssFontFamilyFor = (key) => {
    const k = (key || '').toLowerCase();
    if (k.includes('tiro') || k.includes('times') || k.includes('serif')) {
      return '"Times New Roman", "Liberation Serif", Times, serif';
    }
    if (k.includes('cour') || k.includes('mono')) {
      return '"Courier New", "Liberation Mono", Courier, monospace';
    }
    // helv / lib-sans / default
    return 'Helvetica, "Liberation Sans", Arial, sans-serif';
  };

  // Save state to history
  const saveToHistory = useCallback(() => {
    if (isUndoRedo) return;
    
    const currentState = {
      rectangles: JSON.parse(JSON.stringify(rectangles)),
      textBoxes: JSON.parse(JSON.stringify(textBoxes)),
      circles: JSON.parse(JSON.stringify(circles)),
      lines: JSON.parse(JSON.stringify(lines)),
      arrows: JSON.parse(JSON.stringify(arrows)),
      freehand: JSON.parse(JSON.stringify(freehand))
    };
    
    // Remove any future states if we're not at the end
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(currentState);
    
    // Keep only last 50 states
    if (newHistory.length > 50) {
      newHistory.shift();
    }
    
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [rectangles, textBoxes, circles, lines, arrows, freehand, history, historyIndex, isUndoRedo]);

  // Undo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      setIsUndoRedo(true);
      const prevState = history[historyIndex - 1];
      setRectangles(prevState.rectangles);
      setTextBoxes(prevState.textBoxes);
      setCircles(prevState.circles);
      setLines(prevState.lines);
      setArrows(prevState.arrows);
      setFreehand(prevState.freehand);
      setHistoryIndex(historyIndex - 1);
      setSelectedElement(null);
      toast.success("Undo");
      setTimeout(() => setIsUndoRedo(false), 100);
    }
  }, [history, historyIndex]);

  // Redo
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setIsUndoRedo(true);
      const nextState = history[historyIndex + 1];
      setRectangles(nextState.rectangles);
      setTextBoxes(nextState.textBoxes);
      setCircles(nextState.circles);
      setLines(nextState.lines);
      setArrows(nextState.arrows);
      setFreehand(nextState.freehand);
      setHistoryIndex(historyIndex + 1);
      setSelectedElement(null);
      toast.success("Redo");
      setTimeout(() => setIsUndoRedo(false), 100);
    }
  }, [history, historyIndex]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        handleRedo();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedElement && !editingTextBox) {
          e.preventDefault();
          deleteSelectedElement();
        }
      } else if (!e.ctrlKey && !e.metaKey && !e.altKey && !editingTextBox) {
        // Tool shortcuts (V/M/R/C/L/A/D/T/I) — skip when typing in inputs
        const ae = window.document.activeElement;
        if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable)) return;
        const k = e.key.toLowerCase();
        const map = { v: 'select', q: 'quick_edit', e: 'eraser', m: 'magic_erase', r: 'rectangle', c: 'circle', l: 'line', a: 'arrow', d: 'freehand', t: 'text', i: 'eyedropper' };
        if (map[k]) {
          e.preventDefault();
          setActiveTool(map[k]);
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo, selectedElement, editingTextBox]);

  // Initialize history
  useEffect(() => {
    if (history.length === 0) {
      const initialState = {
        rectangles: {},
        textBoxes: {},
        circles: {},
        lines: {},
        arrows: {},
        freehand: {}
      };
      setHistory([initialState]);
      setHistoryIndex(0);
    }
  }, []);

  // Auto-focus inline edit input
  useEffect(() => {
    if (editingPdfBlock && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingPdfBlock]);

  useEffect(() => {
    fetchPdfData();
  }, [document]);

  useEffect(() => {
    if (pdfData) {
      fetchPageImage(currentPage);
    }
  }, [currentPage, pdfData]);

  // ── Autosave drafts to localStorage ─────────────────────────────────────
  // Protects against lost work if the browser tab reloads or crashes.
  // Drafts are cleared after a successful server-side save.
  const DRAFT_KEY = `taskflow-canvas-draft:${document?.id || 'unknown'}`;
  const autosaveTimer = useRef(null);
  const [draftBanner, setDraftBanner] = useState(null); // { ageMs, draft } | null
  const [draftJustSaved, setDraftJustSaved] = useState(false); // flash indicator
  const justSavedTimer = useRef(null);

  // Snapshot the current edit-state into localStorage (no-op if empty)
  const writeDraft = useCallback(() => {
    if (!document?.id) return;
    const isEmpty =
      Object.values(rectangles).every(a => !a || a.length === 0) &&
      Object.values(textBoxes).every(a => !a || a.length === 0) &&
      Object.values(circles).every(a => !a || a.length === 0) &&
      Object.values(lines).every(a => !a || a.length === 0) &&
      Object.values(arrows).every(a => !a || a.length === 0) &&
      Object.values(freehand).every(a => !a || a.length === 0) &&
      Object.values(pdfTextEdits).every(o => !o || Object.keys(o).length === 0);
    if (isEmpty) {
      try { window.localStorage.removeItem(DRAFT_KEY); } catch (_) {}
      return;
    }
    try {
      const payload = {
        v: 1,
        ts: Date.now(),
        rectangles, textBoxes, circles, lines, arrows, freehand, pdfTextEdits,
        currentPage,
      };
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
      setDraftJustSaved(true);
      if (justSavedTimer.current) clearTimeout(justSavedTimer.current);
      justSavedTimer.current = setTimeout(() => setDraftJustSaved(false), 1400);
    } catch (e) {
      // localStorage may be full / blocked — fail silently
    }
  }, [DRAFT_KEY, document?.id, rectangles, textBoxes, circles, lines, arrows, freehand, pdfTextEdits, currentPage]);

  // Debounced autosave on edit-state change (3s after last change)
  useEffect(() => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(writeDraft, 3000);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
  }, [writeDraft]);

  // Immediate flush on blur / hide / beforeunload
  useEffect(() => {
    const flush = () => writeDraft();
    const onVis = () => { if (window.document.visibilityState === 'hidden') flush(); };
    window.addEventListener('blur', flush);
    window.addEventListener('beforeunload', flush);
    window.document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('blur', flush);
      window.removeEventListener('beforeunload', flush);
      window.document.removeEventListener('visibilitychange', onVis);
    };
  }, [writeDraft]);

  // Check for an existing draft once we know which document we're editing
  useEffect(() => {
    if (!document?.id) return;
    try {
      const raw = window.localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (!draft || !draft.ts) return;
      const ageMs = Date.now() - draft.ts;
      // Show banner if draft is at most 14 days old
      if (ageMs < 14 * 24 * 60 * 60 * 1000) {
        setDraftBanner({ ageMs, draft });
      } else {
        window.localStorage.removeItem(DRAFT_KEY);
      }
    } catch (_) { /* corrupt draft — ignore */ }
  }, [document?.id, DRAFT_KEY]);

  const restoreDraft = () => {
    if (!draftBanner?.draft) return;
    const d = draftBanner.draft;
    setRectangles(d.rectangles || {});
    setTextBoxes(d.textBoxes || {});
    setCircles(d.circles || {});
    setLines(d.lines || {});
    setArrows(d.arrows || {});
    setFreehand(d.freehand || {});
    setPdfTextEdits(d.pdfTextEdits || {});
    if (d.currentPage) setCurrentPage(d.currentPage);
    setDraftBanner(null);
    setTimeout(saveToHistory, 50);
    toast.success('Draft restored');
  };

  const discardDraft = () => {
    try { window.localStorage.removeItem(DRAFT_KEY); } catch (_) {}
    setDraftBanner(null);
  };

  const formatDraftAge = (ms) => {
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  const fetchPdfData = async () => {
    try {
      const response = await axios.get(
        `${API}/documents/${document.id}/pdf-pages`,
        { headers: getAuthHeader() }
      );
      setPdfData(response.data);
      setLoading(false);
    } catch (error) {
      toast.error("Failed to load PDF");
      setLoading(false);
    }
  };

  const fetchPageImage = async (pageNum) => {
    try {
      const response = await axios.get(
        `${API}/documents/${document.id}/pdf-page-image/${pageNum}?t=${Date.now()}`,
        { headers: getAuthHeader() }
      );
      setPageImage(response.data);
      
      // Auto-sample page background color from top area
      try {
        const colorResponse = await axios.post(
          `${API}/documents/${document.id}/sample-color`,
          { page_num: pageNum, x: 50, y: 30 }, // Sample from near top-left (usually background)
          { headers: getAuthHeader() }
        );
        if (colorResponse.data.color) {
          setSampledPageColor(colorResponse.data.color);
          if (usePageColorMode) {
            setFillColor(colorResponse.data.color);
          }
        }
      } catch (colorError) {
        // Silently fail for color sampling
        console.log("Auto color sampling failed:", colorError);
      }
    } catch (error) {
      toast.error("Failed to load page image");
    }
  };

  const getScaleFactor = () => zoom / 100;

  // ── Touch gestures: pinch-to-zoom + two-finger pan ──────────────────────
  // State refs (avoid React re-render on every touchmove)
  const pinchStateRef = useRef(null);

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      // Two-finger gesture: capture initial state
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      const dist = Math.hypot(dx, dy);
      const cx = (t1.clientX + t2.clientX) / 2;
      const cy = (t1.clientY + t2.clientY) / 2;
      const sc = containerRef.current;
      pinchStateRef.current = {
        startDist: dist,
        startZoom: zoom,
        startCx: cx,
        startCy: cy,
        startScrollLeft: sc ? sc.scrollLeft : 0,
        startScrollTop: sc ? sc.scrollTop : 0,
      };
      e.preventDefault();
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && pinchStateRef.current) {
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      const dist = Math.hypot(dx, dy);
      const cx = (t1.clientX + t2.clientX) / 2;
      const cy = (t1.clientY + t2.clientY) / 2;
      const ps = pinchStateRef.current;

      // Pinch → zoom (50–400 %, snap to whole percent)
      const scale = dist / ps.startDist;
      const newZoom = Math.max(50, Math.min(400, Math.round(ps.startZoom * scale)));
      if (newZoom !== zoom) setZoom(newZoom);

      // Two-finger drag → pan via scroll
      const sc = containerRef.current;
      if (sc) {
        sc.scrollLeft = ps.startScrollLeft - (cx - ps.startCx);
        sc.scrollTop = ps.startScrollTop - (cy - ps.startCy);
      }
      e.preventDefault();
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) pinchStateRef.current = null;
  };

  const getCanvasCoords = (e) => {
    if (!canvasRef.current || !pageImage) return { x: 0, y: 0 };
    
    const rect = canvasRef.current.getBoundingClientRect();
    const scale = getScaleFactor();
    
    // Calculate position relative to the overlay div
    const relX = e.clientX - rect.left;
    const relY = e.clientY - rect.top;
    
    // Convert to PDF coordinates (the PDF is displayed at half size due to /2 in width/height)
    // and scaled by zoom level
    const pdfX = relX / scale;
    const pdfY = relY / scale;
    
    return { x: pdfX, y: pdfY };
  };

  // ── Eraser Brush (pointer-events: works on mouse + touch + pen) ───────────
  // Stamps small filled rectangles along the drag path, coloured with the
  // sampled page background. Each stroke produces multiple rects merged into
  // a single Undo step.
  const eraserStamp = (x, y, color) => {
    const size = eraserBrushSize;
    const stamp = {
      x: x - size / 2,
      y: y - size / 2,
      width: size,
      height: size,
      color: color,
      _eraserStroke: true, // marker so we can collapse undos
    };
    setRectangles(prev => ({
      ...prev,
      [currentPage]: [...(prev[currentPage] || []), stamp],
    }));
  };

  const handleEraserPointerDown = (e) => {
    if (activeTool !== 'eraser') return;
    if (e.pointerType === 'mouse' && e.button !== 0) return; // only left mouse
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    e.preventDefault();
    const { x, y } = getCanvasCoords(e);
    const color = sampledPageColor || '#FFFFFF';

    // Smart mode: if we have a detected text bbox, erase that whole word in one tap.
    if (smartEraser && hoveredTextBbox && hoveredTextBbox.width > 0 && hoveredTextBbox.height > 0) {
      const pad = 1;
      const cover = {
        x: hoveredTextBbox.x - pad,
        y: hoveredTextBbox.y - pad,
        width: hoveredTextBbox.width + pad * 2,
        height: hoveredTextBbox.height + pad * 2,
        color,
      };
      setRectangles(prev => ({
        ...prev,
        [currentPage]: [...(prev[currentPage] || []), cover],
      }));
      setHoveredTextBbox(null);
      toast.success(`Erased "${(hoveredTextBbox.text || '').slice(0, 24)}"`, { duration: 1200 });
      setTimeout(saveToHistory, 50);
      return;
    }

    eraserStateRef.current = { active: true, lastX: x, lastY: y, color };
    eraserStamp(x, y, color);
    setEraserPreview({ x, y });
  };

  // Debounced bbox lookup for Smart Eraser
  const requestSmartBboxAt = (x, y) => {
    const slr = smartLookupRef.current;
    if (slr.timer) clearTimeout(slr.timer);
    slr.timer = setTimeout(async () => {
      const myReq = Date.now();
      slr.lastReq = myReq;
      try {
        const res = await axios.post(
          `${API}/documents/${document.id}/text-bbox-at-point`,
          { page_num: currentPage, x, y },
          { headers: getAuthHeader() }
        );
        // Only apply if this is still the latest request
        if (slr.lastReq !== myReq) return;
        const b = res.data;
        if (b && b.width > 0 && b.height > 0) {
          setHoveredTextBbox(b);
        } else {
          setHoveredTextBbox(null);
        }
      } catch (_) {
        if (slr.lastReq === myReq) setHoveredTextBbox(null);
      }
    }, 130);
  };

  const handleEraserPointerMove = (e) => {
    if (activeTool !== 'eraser') return;
    const { x, y } = getCanvasCoords(e);
    // Always update hover preview so the user can see brush size
    setEraserPreview({ x, y });

    // Smart mode: detect text bbox near the cursor (debounced)
    if (smartEraser && !eraserStateRef.current?.active) {
      requestSmartBboxAt(x, y);
      return; // no brush stamping in smart mode
    }

    const s = eraserStateRef.current;
    if (!s || !s.active) return;
    const dx = x - s.lastX;
    const dy = y - s.lastY;
    const dist = Math.hypot(dx, dy);
    // Stamp every brushSize / 3 pixels for a continuous painted line
    const step = Math.max(2, eraserBrushSize / 3);
    if (dist >= step) {
      const steps = Math.floor(dist / step);
      for (let i = 1; i <= steps; i++) {
        const t = (i * step) / dist;
        eraserStamp(s.lastX + dx * t, s.lastY + dy * t, s.color);
      }
      s.lastX = s.lastX + (dx * (steps * step)) / dist;
      s.lastY = s.lastY + (dy * (steps * step)) / dist;
    }
  };

  const handleEraserPointerUp = (e) => {
    if (activeTool !== 'eraser') return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
    if (eraserStateRef.current?.active) {
      eraserStateRef.current = null;
      setTimeout(saveToHistory, 50);
      toast.success(`Erased — ${eraserBrushSize}px brush`, { duration: 1200 });
    }
  };

  const handleEraserPointerLeave = () => {
    if (activeTool !== 'eraser') return;
    setEraserPreview(null);
    setHoveredTextBbox(null);
  };

  const handleCanvasClick = (e) => {
    const { x, y } = getCanvasCoords(e);
    
    if (activeTool === "text") {
      setNewTextPosition({ x, y });
      setAddTextDialog(true);
    } else if (activeTool === "eyedropper") {
      sampleColorAt(x, y);
    } else if (activeTool === "select") {
      const clickedElement = findElementAt(x, y);
      if (clickedElement) {
        setSelectedElement(clickedElement);
        if (clickedElement.type === 'text') {
          setEditingTextBox(clickedElement);
        }
      } else {
        setSelectedElement(null);
        setEditingTextBox(null);
      }
    }
  };

  const handleMouseDown = (e) => {
    const { x, y } = getCanvasCoords(e);
    
    // Check for resize handle click first (when an element is selected)
    if (activeTool === "select" && selectedElement) {
      const handle = getResizeHandleAt(x, y, selectedElement);
      if (handle) {
        setIsResizing(true);
        setResizeHandle(handle);
        setResizeStart({ x, y });
        return;
      }
      
      if (isPointInElement(x, y, selectedElement)) {
        setIsDragging(true);
        const elem = getElement(selectedElement);
        if (elem) {
          setDragOffset({ x: x - (elem.x || elem.cx || elem.x1 || 0), y: y - (elem.y || elem.cy || elem.y1 || 0) });
        }
        return;
      }
    }
    
    if (activeTool === "rectangle" || activeTool === "magic_erase" || activeTool === "quick_edit") {
      setIsDrawing(true);
      setDrawStart({ x, y });
      // Magic Erase / Quick Edit draw a translucent preview rectangle.
      // Quick Edit uses indigo, Magic Erase uses amber.
      let previewColor = fillColor;
      if (activeTool === "magic_erase") previewColor = "rgba(255, 200, 0, 0.35)";
      else if (activeTool === "quick_edit") previewColor = "rgba(99, 102, 241, 0.30)";
      setCurrentShape({
        type: 'rect', x, y, width: 0, height: 0,
        color: previewColor,
        magic: activeTool === "magic_erase",
        quickEdit: activeTool === "quick_edit",
      });
    } else if (activeTool === "circle") {
      setIsDrawing(true);
      setDrawStart({ x, y });
      setCurrentShape({ type: 'circle', cx: x, cy: y, rx: 0, ry: 0, stroke: strokeColor, strokeWidth });
    } else if (activeTool === "line") {
      setIsDrawing(true);
      setDrawStart({ x, y });
      setCurrentShape({ type: 'line', x1: x, y1: y, x2: x, y2: y, stroke: strokeColor, strokeWidth });
    } else if (activeTool === "arrow") {
      setIsDrawing(true);
      setDrawStart({ x, y });
      setCurrentShape({ type: 'arrow', x1: x, y1: y, x2: x, y2: y, stroke: strokeColor, strokeWidth });
    } else if (activeTool === "freehand") {
      setIsDrawing(true);
      setCurrentFreehandPoints([{ x, y }]);
    }
  };

  // Get resize handle at a position
  const getResizeHandleAt = (x, y, element) => {
    const handleSize = 8;
    let bounds = null;
    
    if (element.type === 'rect') {
      const rect = rectangles[element.pageNum]?.[element.index];
      if (rect) bounds = { x: rect.x, y: rect.y, width: rect.width, height: rect.height };
    } else if (element.type === 'circle') {
      const c = circles[element.pageNum]?.[element.index];
      if (c) bounds = { x: c.cx - c.rx, y: c.cy - c.ry, width: c.rx * 2, height: c.ry * 2 };
    }
    
    if (!bounds) return null;
    
    const handles = [
      { name: 'nw', x: bounds.x, y: bounds.y },
      { name: 'n', x: bounds.x + bounds.width / 2, y: bounds.y },
      { name: 'ne', x: bounds.x + bounds.width, y: bounds.y },
      { name: 'e', x: bounds.x + bounds.width, y: bounds.y + bounds.height / 2 },
      { name: 'se', x: bounds.x + bounds.width, y: bounds.y + bounds.height },
      { name: 's', x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height },
      { name: 'sw', x: bounds.x, y: bounds.y + bounds.height },
      { name: 'w', x: bounds.x, y: bounds.y + bounds.height / 2 },
    ];
    
    for (const handle of handles) {
      if (Math.abs(x - handle.x) < handleSize && Math.abs(y - handle.y) < handleSize) {
        return handle.name;
      }
    }
    return null;
  };

  const handleMouseMove = (e) => {
    const { x, y } = getCanvasCoords(e);
    
    if (isResizing && selectedElement && resizeHandle) {
      resizeElement(selectedElement, x, y, resizeHandle);
      return;
    }
    
    if (isDrawing) {
      if (activeTool === "rectangle" || activeTool === "magic_erase" || activeTool === "quick_edit") {
        let previewColor = fillColor;
        if (activeTool === "magic_erase") previewColor = "rgba(255, 200, 0, 0.35)";
        else if (activeTool === "quick_edit") previewColor = "rgba(99, 102, 241, 0.30)";
        setCurrentShape({
          type: 'rect',
          x: Math.min(drawStart.x, x),
          y: Math.min(drawStart.y, y),
          width: Math.abs(x - drawStart.x),
          height: Math.abs(y - drawStart.y),
          color: previewColor,
          magic: activeTool === "magic_erase",
          quickEdit: activeTool === "quick_edit",
        });
      } else if (activeTool === "circle") {
        setCurrentShape({
          type: 'circle',
          cx: (drawStart.x + x) / 2,
          cy: (drawStart.y + y) / 2,
          rx: Math.abs(x - drawStart.x) / 2,
          ry: Math.abs(y - drawStart.y) / 2,
          stroke: strokeColor,
          strokeWidth
        });
      } else if (activeTool === "line") {
        setCurrentShape({
          type: 'line',
          x1: drawStart.x,
          y1: drawStart.y,
          x2: x,
          y2: y,
          stroke: strokeColor,
          strokeWidth
        });
      } else if (activeTool === "arrow") {
        setCurrentShape({
          type: 'arrow',
          x1: drawStart.x,
          y1: drawStart.y,
          x2: x,
          y2: y,
          stroke: strokeColor,
          strokeWidth
        });
      } else if (activeTool === "freehand") {
        setCurrentFreehandPoints(prev => [...prev, { x, y }]);
      }
    } else if (isDragging && selectedElement) {
      updateElementPosition(selectedElement, x - dragOffset.x, y - dragOffset.y);
    }
  };

  const handleMouseUp = async () => {
    if (isResizing) {
      setIsResizing(false);
      setResizeHandle(null);
      setResizeStart(null);
      setTimeout(saveToHistory, 50);
      return;
    }
    
    if (isDragging) {
      setIsDragging(false);
      setTimeout(saveToHistory, 50);
      return;
    }
    
    if (isDrawing) {
      if (activeTool === "rectangle" && currentShape && currentShape.width > 5 && currentShape.height > 5) {
        setRectangles(prev => ({
          ...prev,
          [currentPage]: [...(prev[currentPage] || []), { ...currentShape, type: undefined }]
        }));
        toast.success("Rectangle added");
        setTimeout(saveToHistory, 50);
      } else if (activeTool === "magic_erase" && currentShape && currentShape.width > 5 && currentShape.height > 5) {
        // Capture rect, snapshot it, then async-sample edge color and finalize
        const rect = { x: currentShape.x, y: currentShape.y, width: currentShape.width, height: currentShape.height };
        setIsDrawing(false);
        setCurrentShape(null);
        magicEraseFinalize(rect);
        return;
      } else if (activeTool === "quick_edit" && currentShape && currentShape.width > 5 && currentShape.height > 5) {
        const rect = { x: currentShape.x, y: currentShape.y, width: currentShape.width, height: currentShape.height };
        setIsDrawing(false);
        setCurrentShape(null);
        quickEditFinalize(rect);
        return;
      } else if (activeTool === "quick_edit" && currentShape) {
        // User TAPPED (no drag) — auto-detect text bounding box at the tap point
        // via vector spans → OCR fallback. Mobile-friendly: no need to drag a precise rectangle.
        const tap = { x: drawStart.x, y: drawStart.y };
        setIsDrawing(false);
        setCurrentShape(null);
        try {
          toast.info("Detecting text at tap point…", { duration: 1500 });
          const res = await axios.post(
            `${API}/documents/${document.id}/text-bbox-at-point`,
            { page_num: currentPage, x: tap.x, y: tap.y },
            { headers: getAuthHeader() }
          );
          const b = res.data;
          if (b && b.width > 0 && b.height > 0) {
            quickEditFinalize({ x: b.x, y: b.y, width: b.width, height: b.height });
            if (b.text) {
              // Pre-fill the text input with the detected original word for easy in-place edit
              setTimeout(() => setQuickEditText(b.text), 100);
            }
          }
        } catch (e) {
          // Fallback: use a small box at tap point so user can still edit
          quickEditFinalize({ x: tap.x - 50, y: tap.y - 10, width: 100, height: 20 });
        }
        return;
      } else if (activeTool === "circle" && currentShape && currentShape.rx > 5) {
        setCircles(prev => ({
          ...prev,
          [currentPage]: [...(prev[currentPage] || []), { ...currentShape, type: undefined }]
        }));
        toast.success("Circle added");
        setTimeout(saveToHistory, 50);
      } else if (activeTool === "line" && currentShape) {
        const dx = currentShape.x2 - currentShape.x1;
        const dy = currentShape.y2 - currentShape.y1;
        if (Math.sqrt(dx*dx + dy*dy) > 10) {
          setLines(prev => ({
            ...prev,
            [currentPage]: [...(prev[currentPage] || []), { ...currentShape, type: undefined }]
          }));
          toast.success("Line added");
          setTimeout(saveToHistory, 50);
        }
      } else if (activeTool === "arrow" && currentShape) {
        const dx = currentShape.x2 - currentShape.x1;
        const dy = currentShape.y2 - currentShape.y1;
        if (Math.sqrt(dx*dx + dy*dy) > 10) {
          setArrows(prev => ({
            ...prev,
            [currentPage]: [...(prev[currentPage] || []), { ...currentShape, type: undefined }]
          }));
          toast.success("Arrow added");
          setTimeout(saveToHistory, 50);
        }
      } else if (activeTool === "freehand" && currentFreehandPoints.length > 5) {
        setFreehand(prev => ({
          ...prev,
          [currentPage]: [...(prev[currentPage] || []), { points: currentFreehandPoints, stroke: strokeColor, strokeWidth }]
        }));
        toast.success("Drawing added");
        setTimeout(saveToHistory, 50);
      }
    }
    
    setIsDrawing(false);
    setDrawStart(null);
    setCurrentShape(null);
    setCurrentFreehandPoints([]);
    setIsDragging(false);
  };

  // Resize element based on handle being dragged
  const resizeElement = (element, x, y, handle) => {
    if (element.type === 'rect') {
      setRectangles(prev => {
        const updated = { ...prev };
        const rect = updated[element.pageNum]?.[element.index];
        if (!rect) return prev;
        
        updated[element.pageNum] = [...updated[element.pageNum]];
        const newRect = { ...rect };
        
        // Handle resize based on which corner/edge is being dragged
        if (handle.includes('e')) {
          newRect.width = Math.max(20, x - rect.x);
        }
        if (handle.includes('w')) {
          const newWidth = rect.x + rect.width - x;
          if (newWidth > 20) {
            newRect.x = x;
            newRect.width = newWidth;
          }
        }
        if (handle.includes('s')) {
          newRect.height = Math.max(20, y - rect.y);
        }
        if (handle.includes('n')) {
          const newHeight = rect.y + rect.height - y;
          if (newHeight > 20) {
            newRect.y = y;
            newRect.height = newHeight;
          }
        }
        
        updated[element.pageNum][element.index] = newRect;
        return updated;
      });
    } else if (element.type === 'circle') {
      setCircles(prev => {
        const updated = { ...prev };
        const c = updated[element.pageNum]?.[element.index];
        if (!c) return prev;
        
        updated[element.pageNum] = [...updated[element.pageNum]];
        const newCircle = { ...c };
        
        // Calculate new radii based on handle position
        if (handle === 'e' || handle === 'w') {
          newCircle.rx = Math.max(10, Math.abs(x - c.cx));
        }
        if (handle === 'n' || handle === 's') {
          newCircle.ry = Math.max(10, Math.abs(y - c.cy));
        }
        if (handle === 'ne' || handle === 'se' || handle === 'nw' || handle === 'sw') {
          newCircle.rx = Math.max(10, Math.abs(x - c.cx));
          newCircle.ry = Math.max(10, Math.abs(y - c.cy));
        }
        
        updated[element.pageNum][element.index] = newCircle;
        return updated;
      });
    }
  };

  // Quick Edit dialog state — used by Quick Edit tool (Q)
  const [quickEditDialog, setQuickEditDialog] = useState(false);
  const [quickEditRect, setQuickEditRect] = useState(null);
  const [quickEditText, setQuickEditText] = useState('');
  const [quickEditFont, setQuickEditFont] = useState({ family: 'helv', size: 12, color: '#000000', bold: false });
  const [quickEditPaperColor, setQuickEditPaperColor] = useState('#FFFFFF');
  const [quickEditAnalyzing, setQuickEditAnalyzing] = useState(false);
  const [quickEditDetection, setQuickEditDetection] = useState(null); // { family, confidence, candidates }
  const [availableFonts, setAvailableFonts] = useState([]);
  // Auto-Match toggle — persisted in localStorage so it sticks across sessions
  const [autoMatchOnApply, setAutoMatchOnApply] = useState(() => {
    try { return window.localStorage.getItem('taskflow-quickedit-automatch') === '1'; }
    catch (_) { return false; }
  });
  const toggleAutoMatchOnApply = (val) => {
    setAutoMatchOnApply(val);
    try { window.localStorage.setItem('taskflow-quickedit-automatch', val ? '1' : '0'); } catch (_) {}
  };
  const [flattenOnSave, setFlattenOnSave] = useState(false); // Redaction-proof PDF
  const [showMobileHelp, setShowMobileHelp] = useState(false); // Collapsible help on small screens

  // Version history (last 5 server-saved revisions)
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versionsList, setVersionsList] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const fetchVersions = async () => {
    if (!document?.id) return;
    setVersionsLoading(true);
    try {
      const res = await axios.get(`${API}/documents/${document.id}/versions`, { headers: getAuthHeader() });
      setVersionsList(res.data?.versions || []);
    } catch (_) {
      setVersionsList([]);
    } finally {
      setVersionsLoading(false);
    }
  };
  const openVersionsPanel = async () => {
    setVersionsOpen(true);
    await fetchVersions();
  };
  const downloadVersion = async (version) => {
    if (!document?.id) return;
    try {
      const res = await axios.get(
        `${API}/documents/${document.id}/versions/${version}/download`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      const base = (document.file_name || `doc_${document.id}`).replace(/\.[^.]+$/, '');
      a.download = `${base}_v${version}.pdf`;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 800);
      toast.success(`Downloaded v${version}`);
    } catch (e) {
      toast.error(`Failed to download v${version}`);
    }
  };
  const formatVersionTs = (iso) => {
    try {
      const d = new Date(iso);
      const now = new Date();
      const diff = Math.max(0, (now - d) / 1000);
      if (diff < 60) return `${Math.floor(diff)}s ago`;
      if (diff < 3600) return `${Math.floor(diff/60)} min ago`;
      if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
      return d.toLocaleString();
    } catch (_) { return iso || ''; }
  };

  // Load font catalogue once
  useEffect(() => {
    axios.get(`${API}/pdf-editor/fonts`, { headers: getAuthHeader() })
      .then(r => setAvailableFonts(r.data?.fonts || []))
      .catch(() => {});
  }, []);

  const quickEditFinalize = async (rect) => {
    setQuickEditRect(rect);
    setQuickEditText('');
    setQuickEditFont({ family: 'helv', size: 12, color: '#000000', bold: false });
    setQuickEditPaperColor('#FFFFFF');
    setQuickEditDetection(null);
    setQuickEditDialog(true);
    setQuickEditAnalyzing(true);

    // Run all 3 analyses in parallel — paper color, font detection, font suggestion
    try {
      const [edgeRes, fontRes, suggRes] = await Promise.all([
        axios.post(`${API}/documents/${document.id}/sample-edge-color`,
          { page_num: currentPage, x: rect.x, y: rect.y, width: rect.width, height: rect.height },
          { headers: getAuthHeader() }
        ).catch(() => null),
        axios.post(`${API}/documents/${document.id}/detect-font`,
          { page_num: currentPage, x: rect.x, y: rect.y, width: rect.width, height: rect.height },
          { headers: getAuthHeader() }
        ).catch(() => null),
        axios.post(`${API}/documents/${document.id}/suggest-font`,
          { page_num: currentPage, x: rect.x, y: rect.y, width: rect.width, height: rect.height },
          { headers: getAuthHeader() }
        ).catch(() => null),
      ]);

      const paper = edgeRes?.data?.color || '#FFFFFF';
      const detected = fontRes?.data || {};
      const suggested = suggRes?.data || {};

      setQuickEditPaperColor(paper);
      setQuickEditDetection({
        family: suggested.family || (detected.is_bold ? 'sans' : 'sans'),
        confidence: suggested.confidence || 'low',
        candidates: suggested.candidates || [],
        is_bold: !!detected.is_bold,
      });
      setQuickEditFont({
        family: (suggested.candidates && suggested.candidates[0]) ||
                (detected.is_bold ? 'lib-sans-b' : 'lib-sans'),
        size: detected.font_size || 12,
        color: detected.color || '#000000',
        bold: !!detected.is_bold,
      });
    } catch (e) {
      // Use safe defaults
    } finally {
      setQuickEditAnalyzing(false);
    }
  };

  const applyQuickEdit = () => {
    if (!quickEditRect || !quickEditText.trim()) {
      toast.warning("Type the replacement text first");
      return;
    }
    // 1. Cover the original area with paper-colour rectangle
    const cover = {
      x: quickEditRect.x, y: quickEditRect.y,
      width: quickEditRect.width, height: quickEditRect.height,
      color: quickEditPaperColor,
    };
    // 2. Add the new text on top
    const txt = {
      x: quickEditRect.x + 2,
      y: quickEditRect.y + (quickEditFont.size * 0.78),
      text: quickEditText,
      font_family: quickEditFont.family,
      font_size: quickEditFont.size,
      color: quickEditFont.color,
      bold: quickEditFont.bold,
    };
    setRectangles(prev => ({ ...prev, [currentPage]: [...(prev[currentPage] || []), cover] }));
    setTextBoxes(prev => {
      const list = prev[currentPage] || [];
      const newList = [...list, txt];
      const newIdx = newList.length - 1;
      // Auto-select the just-added text box and switch to select mode so the user
      // can immediately drag-position it on mobile (✎ Edit / ✕ Delete chips visible).
      setTimeout(() => {
        setActiveTool('select');
        setSelectedElement({ type: 'text', index: newIdx, pageNum: currentPage });
        // If user opted-in, run Match Nearby automatically so styling matches surrounding text
        if (autoMatchOnApply) {
          setTimeout(() => matchNearbyTextStyle(newIdx), 60);
        }
      }, 0);
      return { ...prev, [currentPage]: newList };
    });
    setQuickEditDialog(false);
    setQuickEditRect(null);
    setQuickEditText('');
    toast.success(
      autoMatchOnApply
        ? `Replaced text — auto-matched to nearby style`
        : `Replaced text — drag the box to fine-tune position`,
      { duration: 2200 }
    );
    setTimeout(saveToHistory, 50);
  };

  const magicEraseFinalize = async (rect) => {
    // Show progress placeholder rect immediately so user sees instant feedback
    const placeholderId = `me-${Date.now()}`;
    const placeholder = { ...rect, color: '#FFFFFF', _magicPlaceholder: placeholderId };
    setRectangles(prev => ({
      ...prev,
      [currentPage]: [...(prev[currentPage] || []), placeholder],
    }));
    try {
      const res = await axios.post(
        `${API}/documents/${document.id}/sample-edge-color`,
        { page_num: currentPage, x: rect.x, y: rect.y, width: rect.width, height: rect.height },
        { headers: getAuthHeader() }
      );
      const sampled = res.data?.color || '#FFFFFF';
      // Replace placeholder with the proper paper-colored fill
      setRectangles(prev => ({
        ...prev,
        [currentPage]: (prev[currentPage] || []).map(r =>
          r._magicPlaceholder === placeholderId
            ? { x: rect.x, y: rect.y, width: rect.width, height: rect.height, color: sampled }
            : r
        ),
      }));
      toast.success(`Magic Erase: matched paper colour ${sampled} (${res.data?.samples || 0} samples)`);
      setTimeout(saveToHistory, 50);
    } catch (e) {
      // Keep the white placeholder as a fallback so the user's intent isn't lost
      toast.warning('Could not sample paper colour — using white. You can edit colour manually after.');
      setTimeout(saveToHistory, 50);
    }
  };

  const sampleColorAt = async (x, y) => {
    try {
      const response = await axios.post(
        `${API}/documents/${document.id}/sample-color`,
        { page_num: currentPage, x, y },
        { headers: getAuthHeader() }
      );
      if (response.data.color) {
        setSampledPageColor(response.data.color);
        if (usePageColorMode) {
          setFillColor(response.data.color);
        }
        toast.success(`Page color sampled: ${response.data.color} - ${usePageColorMode ? 'Applied to fill' : 'Click "Use Page Color" to apply'}`);
      }
    } catch (error) {
      setSampledPageColor("#FFFFFF");
      toast.info("Color sampling not available, using white");
    }
  };

  const findElementAt = (x, y) => {
    // Check rectangles
    const pageRects = rectangles[currentPage] || [];
    for (let i = pageRects.length - 1; i >= 0; i--) {
      const rect = pageRects[i];
      if (x >= rect.x && x <= rect.x + rect.width && y >= rect.y && y <= rect.y + rect.height) {
        return { type: 'rect', index: i, pageNum: currentPage };
      }
    }
    
    // Check circles
    const pageCircles = circles[currentPage] || [];
    for (let i = pageCircles.length - 1; i >= 0; i--) {
      const c = pageCircles[i];
      const dx = (x - c.cx) / c.rx;
      const dy = (y - c.cy) / c.ry;
      if (dx * dx + dy * dy <= 1) {
        return { type: 'circle', index: i, pageNum: currentPage };
      }
    }
    
    // Check text boxes
    const pageTexts = textBoxes[currentPage] || [];
    for (let i = pageTexts.length - 1; i >= 0; i--) {
      const text = pageTexts[i];
      const fs = text.font_size || text.fontSize || 14;
      const width = Math.max(20, (text.text || '').length * fs * 0.6);
      const height = fs * 1.5;
      if (x >= text.x && x <= text.x + width && y >= text.y - height && y <= text.y + 4) {
        return { type: 'text', index: i, pageNum: currentPage };
      }
    }
    
    return null;
  };

  const isPointInElement = (x, y, element) => {
    if (element.type === 'rect') {
      const rect = rectangles[element.pageNum]?.[element.index];
      if (!rect) return false;
      return x >= rect.x && x <= rect.x + rect.width && y >= rect.y && y <= rect.y + rect.height;
    } else if (element.type === 'circle') {
      const c = circles[element.pageNum]?.[element.index];
      if (!c) return false;
      const dx = (x - c.cx) / c.rx;
      const dy = (y - c.cy) / c.ry;
      return dx * dx + dy * dy <= 1;
    } else if (element.type === 'text') {
      const text = textBoxes[element.pageNum]?.[element.index];
      if (!text) return false;
      const fs = text.font_size || text.fontSize || 14;
      const width = Math.max(20, (text.text || '').length * fs * 0.6);
      const height = fs * 1.5;
      return x >= text.x && x <= text.x + width && y >= text.y - height && y <= text.y + 4;
    }
    return false;
  };

  const getElement = (element) => {
    if (element.type === 'rect') return rectangles[element.pageNum]?.[element.index];
    if (element.type === 'circle') return circles[element.pageNum]?.[element.index];
    if (element.type === 'text') return textBoxes[element.pageNum]?.[element.index];
    if (element.type === 'line') return lines[element.pageNum]?.[element.index];
    if (element.type === 'arrow') return arrows[element.pageNum]?.[element.index];
    return null;
  };

  const updateElementPosition = (element, newX, newY) => {
    if (element.type === 'rect') {
      setRectangles(prev => {
        const updated = { ...prev };
        if (updated[element.pageNum]?.[element.index]) {
          updated[element.pageNum] = [...updated[element.pageNum]];
          updated[element.pageNum][element.index] = { ...updated[element.pageNum][element.index], x: newX, y: newY };
        }
        return updated;
      });
    } else if (element.type === 'circle') {
      setCircles(prev => {
        const updated = { ...prev };
        if (updated[element.pageNum]?.[element.index]) {
          updated[element.pageNum] = [...updated[element.pageNum]];
          updated[element.pageNum][element.index] = { ...updated[element.pageNum][element.index], cx: newX, cy: newY };
        }
        return updated;
      });
    } else if (element.type === 'text') {
      setTextBoxes(prev => {
        const updated = { ...prev };
        if (updated[element.pageNum]?.[element.index]) {
          updated[element.pageNum] = [...updated[element.pageNum]];
          updated[element.pageNum][element.index] = { ...updated[element.pageNum][element.index], x: newX, y: newY };
        }
        return updated;
      });
    }
  };

  const handleAddText = () => {
    if (!newText.trim()) return;
    
    const textBox = {
      x: newTextPosition.x,
      y: newTextPosition.y,
      text: newText,
      fontSize: fontSize,
      color: textColor
    };
    
    setTextBoxes(prev => {
      const list = prev[currentPage] || [];
      const newList = [...list, textBox];
      // Auto-select the just-added text and switch to Select mode so the user
      // can immediately drag-position it (✎ Edit / ✕ Delete chips visible).
      setTimeout(() => {
        setActiveTool('select');
        setSelectedElement({ type: 'text', index: newList.length - 1, pageNum: currentPage });
      }, 0);
      return { ...prev, [currentPage]: newList };
    });
    
    setAddTextDialog(false);
    setNewText("");
    toast.success("Text added — drag to position");
    setTimeout(saveToHistory, 50);
  };

  const updateTextBox = (index, newText) => {
    setTextBoxes(prev => {
      const updated = { ...prev };
      if (updated[currentPage]?.[index]) {
        updated[currentPage] = [...updated[currentPage]];
        updated[currentPage][index] = { ...updated[currentPage][index], text: newText };
      }
      return updated;
    });
  };

  // Generic patch for a text box's typographic properties (font, size, bold, color)
  // Used by the inline mini-toolbar that appears with the selected text.
  const patchTextBoxProps = (index, patch) => {
    setTextBoxes(prev => {
      const list = prev[currentPage];
      if (!list?.[index]) return prev;
      const current = list[index];
      const updatedItem = { ...current, ...patch };
      // Keep both legacy and snake_case keys in sync so renderer + backend both pick it up
      if (patch.font_size !== undefined) updatedItem.fontSize = patch.font_size;
      if (patch.fontSize !== undefined) updatedItem.font_size = patch.fontSize;
      const next = { ...prev, [currentPage]: [...list] };
      next[currentPage][index] = updatedItem;
      return next;
    });
    setTimeout(saveToHistory, 50);
  };

  // "Match nearby" — copy font/size/bold/color from the closest original text block on the page.
  // Maps PyMuPDF font names → our internal font keys so it round-trips through save.
  const mapPyMuPdfFontToKey = (rawName, flags) => {
    const fn = (rawName || '').toLowerCase();
    const isBold = !!(flags & 16) || fn.includes('bold');
    const isItalic = !!(flags & 2) || fn.includes('italic') || fn.includes('oblique');
    if (fn.includes('times') || fn.includes('serif')) {
      if (isBold && isItalic) return 'tibi';
      if (isBold) return 'tibo';
      if (isItalic) return 'tiit';
      return 'tiro';
    }
    if (fn.includes('cour') || fn.includes('mono')) {
      if (isBold && isItalic) return 'cobi';
      if (isBold) return 'cobo';
      if (isItalic) return 'coit';
      return 'cour';
    }
    // Helvetica / sans-serif family
    if (isBold && isItalic) return 'hebi';
    if (isBold) return 'hebo';
    if (isItalic) return 'heit';
    return 'helv';
  };

  const matchNearbyTextStyle = (idx) => {
    const textBox = textBoxes[currentPage]?.[idx];
    if (!textBox) return;
    const blocks = pdfData?.pages?.[currentPage - 1]?.text_blocks || [];
    if (blocks.length === 0) {
      toast.warning('No original text on this page to match from');
      return;
    }
    // Use the text-box centre as the anchor
    const fs = textBox.font_size || textBox.fontSize || 12;
    const ax = textBox.x;
    const ay = textBox.y - fs / 2;
    // Find nearest block by centre-to-centre distance
    let best = null;
    let bestDist = Infinity;
    for (const b of blocks) {
      const bx = b.x + (b.width || 0) / 2;
      const by = b.y + (b.height || 0) / 2;
      const d = Math.hypot(bx - ax, by - ay);
      if (d < bestDist) { bestDist = d; best = b; }
    }
    if (!best) {
      toast.warning('Could not find nearby text to match');
      return;
    }
    const fontKey = mapPyMuPdfFontToKey(best.font, best.flags);
    const colorHex = best.color !== undefined
      ? `#${((best.color || 0) >>> 0).toString(16).padStart(6, '0').slice(-6)}`
      : (textBox.color || '#000000');
    const bold = !!(best.flags & 16);
    const size = Math.max(6, +(best.size || fs).toFixed(1));
    patchTextBoxProps(idx, {
      font_family: fontKey,
      font_size: size,
      bold,
      color: colorHex,
    });
    toast.success(`Matched style — ${size.toFixed(1)}pt ${fontKey}${bold ? ' bold' : ''}`, { duration: 1500 });
  };

  const deleteSelectedElement = () => {
    if (!selectedElement) return;
    
    const { type, pageNum, index } = selectedElement;
    
    if (type === 'rect') {
      setRectangles(prev => ({
        ...prev,
        [pageNum]: prev[pageNum].filter((_, i) => i !== index)
      }));
    } else if (type === 'circle') {
      setCircles(prev => ({
        ...prev,
        [pageNum]: prev[pageNum].filter((_, i) => i !== index)
      }));
    } else if (type === 'text') {
      setTextBoxes(prev => ({
        ...prev,
        [pageNum]: prev[pageNum].filter((_, i) => i !== index)
      }));
    } else if (type === 'line') {
      setLines(prev => ({
        ...prev,
        [pageNum]: prev[pageNum].filter((_, i) => i !== index)
      }));
    } else if (type === 'arrow') {
      setArrows(prev => ({
        ...prev,
        [pageNum]: prev[pageNum].filter((_, i) => i !== index)
      }));
    }
    
    setSelectedElement(null);
    toast.success("Element deleted");
    setTimeout(saveToHistory, 50);
  };

  const handleSave = async (saveToLibrary = false) => {
    setSaving(true);
    try {
      const payload = {
        rectangles: rectangles,
        text_boxes: textBoxes,
        circles: circles,
        lines: lines,
        arrows: arrows,
        freehand: freehand,
        pdf_text_edits: pdfTextEdits,
        flatten_edits: flattenOnSave,
      };
      
      await axios.post(
        `${API}/documents/${document.id}/canvas-edit-save`,
        payload,
        { headers: getAuthHeader() }
      );
      
      // Clear edits after successful save
      setRectangles({});
      setTextBoxes({});
      setCircles({});
      setLines({});
      setArrows({});
      setFreehand({});
      setPdfTextEdits({});
      setHistory([{ rectangles: {}, textBoxes: {}, circles: {}, lines: {}, arrows: {}, freehand: {} }]);
      setHistoryIndex(0);
      
      // Clear the localStorage draft now that everything is safely on the server
      try { window.localStorage.removeItem(DRAFT_KEY); } catch (_) {}
      setDraftBanner(null);
      
      await fetchPageImage(currentPage);
      
      toast.success("PDF saved successfully!");
      
      if (saveToLibrary) {
        try {
          await axios.post(
            `${API}/documents/${document.id}/save-to-library`,
            { library_name: document.file_name, tags: [] },
            { headers: getAuthHeader() }
          );
          toast.success(`Saved '${document.file_name}' to Document Library!`);
        } catch (err) {
          toast.error("Failed to save to library");
        }
        onSave?.();
      } else {
        setSaveToLibraryDialog(true);
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.response?.data?.detail || "Failed to save PDF");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveToLibraryFromDialog = async () => {
    try {
      await axios.post(
        `${API}/documents/${document.id}/save-to-library`,
        { library_name: document.file_name, tags: [] },
        { headers: getAuthHeader() }
      );
      toast.success(`Saved '${document.file_name}' to Document Library!`);
    } catch (err) {
      toast.error("Failed to save to library");
    } finally {
      setSaveToLibraryDialog(false);
      onSave?.();
    }
  };

  // Open PDF in new tab for print preview
  const handlePrintPreview = async () => {
    try {
      const totalEdits = getTotalEdits();
      let response;
      
      if (totalEdits > 0) {
        // Generate preview with pending canvas edits
        toast.info("Generating preview with your canvas edits...");
        response = await axios.post(
          `${API}/documents/${document.id}/canvas-edit-save`,
          {
            rectangles: rectangles,
            textBoxes: textBoxes,
            circles: circles,
            lines: lines,
            arrows: arrows,
            freehand: freehand,
            preview_only: true  // Flag to return PDF without saving
          },
          { 
            headers: getAuthHeader(),
            responseType: 'blob'
          }
        );
        toast.success("Preview generated with canvas edits!");
      } else {
        // No pending edits, get the saved PDF
        response = await axios.get(
          `${API}/documents/${document.id}/download/converted`,
          { 
            headers: getAuthHeader(),
            responseType: 'blob'
          }
        );
      }
      
      // Create blob URL and open in new tab
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      
      // Optional: Auto-trigger print dialog after PDF loads
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      }
    } catch (error) {
      console.error("Print preview error:", error);
      toast.error("Failed to open print preview");
    }
  };

  const [compareOrigUrl, setCompareOrigUrl] = useState(null);

  const handleCompare = async () => {
    if (getTotalEdits() === 0) {
      toast.info("No edits yet — add some changes to compare.");
      return;
    }
    setComparing(true);
    try {
      // Fetch BOTH original and edited preview as blobs in parallel
      const [origRes, editedRes] = await Promise.all([
        axios.get(`${API}/documents/${document.id}/download/converted`, { headers: getAuthHeader(), responseType: 'blob' }),
        axios.post(
          `${API}/documents/${document.id}/canvas-edit-save`,
          { rectangles, textBoxes, circles, lines, arrows, freehand, preview_only: true },
          { headers: getAuthHeader(), responseType: 'blob' }
        ),
      ]);
      // Revoke previous URLs if any
      if (comparePreviewUrl) URL.revokeObjectURL(comparePreviewUrl);
      if (compareOrigUrl) URL.revokeObjectURL(compareOrigUrl);
      const origUrl = URL.createObjectURL(new Blob([origRes.data], { type: 'application/pdf' }));
      const editedUrl = URL.createObjectURL(new Blob([editedRes.data], { type: 'application/pdf' }));
      setCompareOrigUrl(origUrl);
      setComparePreviewUrl(editedUrl);
      setCompareOpen(true);
    } catch (e) {
      toast.error("Failed to build compare preview");
    } finally {
      setComparing(false);
    }
  };

  const closeCompare = () => {
    setCompareOpen(false);
    if (comparePreviewUrl) { URL.revokeObjectURL(comparePreviewUrl); setComparePreviewUrl(null); }
    if (compareOrigUrl) { URL.revokeObjectURL(compareOrigUrl); setCompareOrigUrl(null); }
  };

  const getTotalEdits = () => {
    let total = 0;
    Object.values(rectangles).forEach(arr => total += arr.length);
    Object.values(textBoxes).forEach(arr => total += arr.length);
    Object.values(circles).forEach(arr => total += arr.length);
    Object.values(lines).forEach(arr => total += arr.length);
    Object.values(arrows).forEach(arr => total += arr.length);
    Object.values(freehand).forEach(arr => total += arr.length);
    // Inline PDF text replacements (vector tap-to-edit) — each replaced block counts as 1 edit
    Object.values(pdfTextEdits).forEach(pageEdits => total += Object.keys(pageEdits || {}).length);
    return total;
  };

  const tools = [
    { id: "select", icon: MousePointer, label: "Select & Move (V)" },
    { id: "quick_edit", icon: Edit3, label: "Quick Edit Text (Q) — drag to replace any text" },
    { id: "eraser", icon: Eraser, label: "Eraser Brush (E) — paint to erase (works on touch)" },
    { id: "magic_erase", icon: Wand2, label: "Magic Erase (M) — drag a box to auto-blend" },
    { id: "rectangle", icon: Square, label: "Cover Rectangle (R)" },
    { id: "circle", icon: Circle, label: "Circle (C)" },
    { id: "line", icon: Minus, label: "Line (L)" },
    { id: "arrow", icon: ArrowRight, label: "Arrow (A)" },
    { id: "freehand", icon: Pencil, label: "Freehand Draw (D)" },
    { id: "text", icon: Type, label: "Add Text (T)" },
    { id: "eyedropper", icon: Pipette, label: "Pick Color (I)" },
  ];

  const fillColors = ["#FFFFFF", "#F3F4F6", "#D1D5DB", "#FEF3C7", "#DBEAFE", "#FCE7F3", "#D1FAE5"];
  const strokeColors = ["#FF0000", "#0000FF", "#00FF00", "#FFA500", "#800080", "#000000", "#FFFFFF"];

  // Render arrow head
  const renderArrowHead = (x1, y1, x2, y2, color, scale) => {
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const headLength = 10 * scale;
    const points = [
      { x: x2 * scale, y: y2 * scale },
      { x: (x2 - headLength * Math.cos(angle - Math.PI/6)) * scale / scale * scale, y: (y2 - headLength * Math.sin(angle - Math.PI/6)) * scale / scale * scale },
      { x: (x2 - headLength * Math.cos(angle + Math.PI/6)) * scale / scale * scale, y: (y2 - headLength * Math.sin(angle + Math.PI/6)) * scale / scale * scale }
    ];
    return `M ${x2 * scale} ${y2 * scale} L ${(x2 - headLength * Math.cos(angle - Math.PI/6))} ${(y2 - headLength * Math.sin(angle - Math.PI/6))} M ${x2 * scale} ${y2 * scale} L ${(x2 - headLength * Math.cos(angle + Math.PI/6))} ${(y2 - headLength * Math.sin(angle + Math.PI/6))}`;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="p-8">
          <Loader2 className="w-8 h-8 animate-spin mx-auto" />
          <p className="mt-2 text-center">Loading Canvas Editor...</p>
        </Card>
      </div>
    );
  }

  const scale = getScaleFactor();

  return (
    <div className="fixed inset-0 bg-slate-900 flex flex-col z-50" data-testid="canvas-pdf-editor">
      {/* Version history panel */}
      {versionsOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-[60] flex items-start justify-center pt-16 px-4"
          onClick={() => setVersionsOpen(false)}
          data-testid="version-history-overlay"
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[70vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50 rounded-t-lg">
              <div>
                <h3 className="font-semibold text-sm">Version History</h3>
                <p className="text-xs text-slate-500">Last 5 saved revisions of this document</p>
              </div>
              <button
                onClick={() => setVersionsOpen(false)}
                className="p-1 rounded hover:bg-slate-200"
                data-testid="version-history-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-3">
              {versionsLoading ? (
                <div className="py-8 flex items-center justify-center text-slate-500 text-sm">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
                </div>
              ) : versionsList.length === 0 ? (
                <div className="py-8 text-center text-slate-500 text-sm">
                  No previous versions yet. Each time you Save, a snapshot is archived here.
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {versionsList.map((v) => (
                    <li
                      key={v.version}
                      className="py-2 flex items-center justify-between gap-2"
                      data-testid={`version-row-${v.version}`}
                    >
                      <div className="min-w-0">
                        <div className="font-medium text-sm">
                          Version {v.version}
                          <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 align-middle">
                            {v.source === 'canvas' ? 'Image edit' : 'Text edit'}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500">
                          {formatVersionTs(v.ts)} · {(v.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => downloadVersion(v.version)}
                        className="h-7 px-3 bg-slate-700 hover:bg-slate-800 text-white text-xs"
                        data-testid={`version-download-${v.version}`}
                      >
                        Download
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Draft restore banner — shown when a localStorage draft was found on mount */}
      {draftBanner && (
        <div
          className="bg-amber-100 border-b border-amber-300 px-3 py-2 flex items-center justify-between gap-3 text-sm"
          data-testid="draft-restore-banner"
        >
          <span className="text-amber-900 truncate">
            <strong>Unsaved draft found</strong> from {formatDraftAge(draftBanner.ageMs)} — restore your previous edits?
          </span>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              onClick={restoreDraft}
              className="bg-amber-600 hover:bg-amber-700 text-white h-7 px-3"
              data-testid="draft-restore-btn"
            >
              Restore
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={discardDraft}
              className="text-amber-900 hover:bg-amber-200 h-7 px-2"
              data-testid="draft-discard-btn"
            >
              Discard
            </Button>
          </div>
        </div>
      )}
      {/* Header Toolbar */}
      <div className="bg-white border-b px-2 sm:px-4 py-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-canvas-editor" className="px-2 sm:px-3">
            <X className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Close</span>
          </Button>
          <span className="font-medium text-xs sm:text-sm truncate max-w-[120px] sm:max-w-xs">{document.file_name}</span>
          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full whitespace-nowrap">
            Canvas Editor
          </span>
          {onSwitchToTextEditor && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onSwitchToTextEditor();
                toast.info('Switched to Text Editor — tap any word to edit directly', { duration: 2200 });
              }}
              data-testid="switch-to-text-editor"
              className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 whitespace-nowrap"
              title="Switch to the vector Text Editor for direct word-by-word typing (recommended for vector PDFs)"
            >
              <Type className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline text-xs">Use Text Editor</span>
            </Button>
          )}
        </div>

        <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
          {/* Undo/Redo */}
          <div className="flex items-center gap-1 border-r pr-2 mr-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleUndo} 
              disabled={historyIndex <= 0}
              title="Undo (Ctrl+Z)"
              data-testid="undo-btn"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRedo} 
              disabled={historyIndex >= history.length - 1}
              title="Redo (Ctrl+Y)"
              data-testid="redo-btn"
            >
              <Redo2 className="w-4 h-4" />
            </Button>
          </div>
          
          {selectedElement && (
            <Button variant="outline" size="sm" onClick={deleteSelectedElement} className="text-red-600" data-testid="delete-element-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
          
          <div className="flex items-center gap-2 border-l pl-4 ml-2">
            <Button variant="outline" size="sm" onClick={() => setZoom(Math.max(50, zoom - 25))}>
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-sm w-12 text-center">{zoom}%</span>
            <Button variant="outline" size="sm" onClick={() => setZoom(Math.min(200, zoom + 25))}>
              <ZoomIn className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="flex items-center gap-2 border-l pl-4 ml-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm">
              {currentPage} / {pdfData?.total_pages || 1}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setCurrentPage(Math.min(pdfData?.total_pages || 1, currentPage + 1))}
              disabled={currentPage >= (pdfData?.total_pages || 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="flex items-center gap-2 ml-4">
            <label
              className="hidden sm:flex items-center gap-1.5 text-xs text-slate-700 px-2 py-1 rounded border border-slate-200 cursor-pointer hover:bg-slate-50"
              title="Rasterise edited pages so the original text cannot be extracted from the saved PDF"
              data-testid="canvas-flatten-toggle"
            >
              <input
                type="checkbox"
                checked={flattenOnSave}
                onChange={e => setFlattenOnSave(e.target.checked)}
                className="rounded text-red-600 focus:ring-red-500"
              />
              <span>{flattenOnSave ? '🔒 Redaction-proof' : 'Redaction-proof'}</span>
            </label>
            <Button 
              onClick={() => handleSave(false)} 
              disabled={saving || getTotalEdits() === 0}
              className={(flattenOnSave ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700') + ' relative'}
              data-testid="save-canvas-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {flattenOnSave ? 'Save Sealed PDF' : 'Save PDF'}
              {getTotalEdits() > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 bg-white/95 text-green-700 text-[11px] font-bold rounded-full">
                  {getTotalEdits()}
                </span>
              )}
            </Button>
            {getTotalEdits() > 0 && (
              <button
                onClick={handleUndo}
                disabled={historyIndex <= 0}
                className="ml-1 px-2 py-1 text-xs rounded-full border border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                data-testid="canvas-undo-chip"
                title="Undo most-recent edit"
              >
                ↶ Undo
              </button>
            )}
            {getTotalEdits() > 0 && (
              <span
                className={`ml-1 px-2 py-1 text-[10px] rounded-full whitespace-nowrap transition-colors ${
                  draftJustSaved
                    ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                    : 'bg-slate-100 text-slate-500 border border-slate-200'
                }`}
                data-testid="canvas-autosave-pill"
                title="Your edits are auto-saved locally — they'll be restored if you reload"
              >
                {draftJustSaved ? '✓ Draft saved' : 'Auto-saving…'}
              </span>
            )}
            <Button 
              onClick={() => handleSave(true)} 
              disabled={saving || getTotalEdits() === 0}
              variant="outline"
              className="border-purple-600 text-purple-600 hover:bg-purple-50"
            >
              <FolderOpen className="w-4 h-4 mr-2" />
              Save to Library
            </Button>
            <Button
              onClick={openVersionsPanel}
              variant="outline"
              size="sm"
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="version-history-btn"
              title="View last 5 saved versions"
            >
              <RotateCcw className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">History</span>
            </Button>
            <Button 
              onClick={handleCompare}
              disabled={comparing || getTotalEdits() === 0}
              variant="outline"
              className="border-amber-500 text-amber-700 hover:bg-amber-50"
              data-testid="compare-btn"
              title="Side-by-side: original vs your edits"
            >
              {comparing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <span className="mr-2">⇆</span>}
              Compare
            </Button>
            <Button 
              onClick={handlePrintPreview}
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
              data-testid="print-preview-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              {getTotalEdits() > 0 ? `Preview Changes (${getTotalEdits()})` : 'Print Preview'}
            </Button>
          </div>
        </div>
      </div>

      {/* Quick-Edit hint banner — only when Quick Edit tool is active */}
      {activeTool === "quick_edit" && (
        <div className="bg-indigo-100 border-b border-indigo-300 text-indigo-900 text-xs sm:text-sm px-3 py-2 flex items-center gap-2" data-testid="quick-edit-hint">
          <Sparkles className="w-4 h-4 flex-shrink-0" />
          <span>
            <strong>Quick Edit:</strong> Tap a word to auto-select it, OR drag a rectangle around any text.
            The system auto-detects font, size &amp; paper colour for you.
          </span>
        </div>
      )}

      {/* Tools Sidebar & Canvas */}
      <div className="flex flex-col sm:flex-row flex-1 overflow-hidden">
        {/* Tools Sidebar — vertical on desktop, horizontal scrollable strip on mobile */}
        <div className="w-full sm:w-16 bg-white border-b sm:border-b-0 sm:border-r flex sm:flex-col items-center py-2 sm:py-4 gap-1 overflow-x-auto sm:overflow-x-visible sm:overflow-y-auto flex-shrink-0">
          {tools.map(tool => (
            <Button
              key={tool.id}
              variant={activeTool === tool.id ? "default" : "ghost"}
              size="sm"
              className={`w-10 h-10 sm:w-12 sm:h-12 p-0 flex-shrink-0 ${activeTool === tool.id ? 'bg-blue-600' : ''}`}
              onClick={() => setActiveTool(tool.id)}
              title={tool.label}
              data-testid={`tool-${tool.id}`}
            >
              <tool.icon className="w-5 h-5" />
            </Button>
          ))}

          <div className="border-l sm:border-l-0 sm:border-t mx-1 sm:mx-0 sm:my-2 h-8 sm:h-auto sm:w-10"></div>
          
          {/* Page Color Section */}
          <span className="text-xs text-slate-500 text-center leading-tight w-full px-1">Page Color</span>
          <div className="flex flex-col gap-1 items-center">
            {sampledPageColor ? (
              <>
                <div
                  className={`w-8 h-8 rounded border-2 ${usePageColorMode ? 'border-green-500 ring-2 ring-green-300' : 'border-slate-400'}`}
                  style={{ backgroundColor: sampledPageColor }}
                  title={`Sampled: ${sampledPageColor}`}
                />
                <button
                  onClick={() => {
                    setUsePageColorMode(!usePageColorMode);
                    if (!usePageColorMode) {
                      setFillColor(sampledPageColor);
                      toast.success("Page color mode ON - shapes will match page background");
                    } else {
                      toast.info("Page color mode OFF");
                    }
                  }}
                  className={`text-xs px-1 py-0.5 rounded ${usePageColorMode ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-600'}`}
                  title={usePageColorMode ? "Click to disable page color mode" : "Click to use this color for shapes"}
                >
                  {usePageColorMode ? 'ON' : 'Use'}
                </button>
              </>
            ) : (
              <span className="text-xs text-slate-400 text-center">Click page to sample</span>
            )}
          </div>
          
          <div className="border-t my-2 w-10"></div>
          <span className="text-xs text-slate-500">Fill</span>
          <div className="flex flex-col gap-1">
            {fillColors.map(color => (
              <button
                key={`fill-${color}`}
                className={`w-6 h-6 rounded border-2 ${fillColor === color && !usePageColorMode ? 'border-blue-600 ring-2 ring-blue-300' : 'border-gray-300'}`}
                style={{ backgroundColor: color }}
                onClick={() => {
                  setFillColor(color);
                  setUsePageColorMode(false);
                }}
              />
            ))}
          </div>
          
          <div className="border-t my-2 w-10"></div>
          <span className="text-xs text-slate-500">Stroke</span>
          <div className="flex flex-col gap-1">
            {strokeColors.map(color => (
              <button
                key={`stroke-${color}`}
                className={`w-6 h-6 rounded border-2 ${strokeColor === color ? 'border-blue-600 ring-2 ring-blue-300' : 'border-gray-300'}`}
                style={{ backgroundColor: color }}
                onClick={() => setStrokeColor(color)}
              />
            ))}
          </div>
        </div>

        {/* Canvas Area */}
        <div
          ref={containerRef}
          className="flex-1 overflow-auto bg-slate-700 p-8"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          style={{ touchAction: 'manipulation' }}
        >
          <div 
            className="relative mx-auto bg-white shadow-2xl"
            style={{
              width: pageImage ? (pageImage.width / 2) * scale : 600,
              height: pageImage ? (pageImage.height / 2) * scale : 800,
            }}
          >
            {/* PDF Page Image */}
            {pageImage && (
              <img
                src={pageImage.image}
                alt={`Page ${currentPage}`}
                className="w-full h-full pointer-events-none select-none"
                draggable={false}
              />
            )}

            {/* Interactive overlay for mouse events */}
            <div
              ref={canvasRef}
              className="absolute inset-0"
              onClick={handleCanvasClick}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onPointerDown={handleEraserPointerDown}
              onPointerMove={handleEraserPointerMove}
              onPointerUp={handleEraserPointerUp}
              onPointerCancel={handleEraserPointerUp}
              onPointerLeave={handleEraserPointerLeave}
              style={{ 
                cursor: activeTool === "select" ? "default" : 
                        activeTool === "eraser" ? "none" :
                        activeTool === "rectangle" || activeTool === "circle" || activeTool === "magic_erase" || activeTool === "quick_edit" ? "crosshair" : 
                        activeTool === "line" || activeTool === "arrow" || activeTool === "freehand" ? "crosshair" :
                        activeTool === "text" ? "text" :
                        activeTool === "eyedropper" ? "crosshair" : "default",
                touchAction: activeTool === "eraser" ? "none" : undefined,
              }}
            />

            {/* Eraser brush hover indicator (visible only when eraser tool is active) */}
            {activeTool === "eraser" && eraserPreview && !(smartEraser && hoveredTextBbox) && (
              <div
                className="absolute rounded-full border-2 border-blue-500 bg-blue-200/30 pointer-events-none shadow-sm"
                style={{
                  left: eraserPreview.x * scale - (eraserBrushSize * scale) / 2,
                  top: eraserPreview.y * scale - (eraserBrushSize * scale) / 2,
                  width: eraserBrushSize * scale,
                  height: eraserBrushSize * scale,
                }}
                data-testid="eraser-preview"
              />
            )}

            {/* Smart-eraser bbox highlight — shown when smart mode finds a word under the cursor */}
            {activeTool === "eraser" && smartEraser && hoveredTextBbox && (
              <div
                className="absolute border-2 border-fuchsia-500 bg-fuchsia-300/30 pointer-events-none rounded-sm shadow-sm"
                style={{
                  left: hoveredTextBbox.x * scale,
                  top: hoveredTextBbox.y * scale,
                  width: hoveredTextBbox.width * scale,
                  height: hoveredTextBbox.height * scale,
                }}
                data-testid="smart-eraser-bbox"
              />
            )}

            {/* SVG overlay for shapes */}
            <svg 
              className="absolute inset-0 pointer-events-none" 
              style={{ width: '100%', height: '100%' }}
              viewBox={`0 0 ${(pageImage?.width || 600) / 2} ${(pageImage?.height || 800) / 2}`}
            >
              {/* Current drawing shape */}
              {currentShape?.type === 'circle' && (
                <ellipse
                  cx={currentShape.cx}
                  cy={currentShape.cy}
                  rx={currentShape.rx}
                  ry={currentShape.ry}
                  fill="none"
                  stroke={currentShape.stroke}
                  strokeWidth={currentShape.strokeWidth}
                  strokeDasharray="5,5"
                />
              )}
              {currentShape?.type === 'line' && (
                <line
                  x1={currentShape.x1}
                  y1={currentShape.y1}
                  x2={currentShape.x2}
                  y2={currentShape.y2}
                  stroke={currentShape.stroke}
                  strokeWidth={currentShape.strokeWidth}
                  strokeDasharray="5,5"
                />
              )}
              {currentShape?.type === 'arrow' && (
                <>
                  <line
                    x1={currentShape.x1}
                    y1={currentShape.y1}
                    x2={currentShape.x2}
                    y2={currentShape.y2}
                    stroke={currentShape.stroke}
                    strokeWidth={currentShape.strokeWidth}
                    strokeDasharray="5,5"
                  />
                </>
              )}
              
              {/* Freehand current drawing */}
              {currentFreehandPoints.length > 1 && (
                <polyline
                  points={currentFreehandPoints.map(p => `${p.x},${p.y}`).join(' ')}
                  fill="none"
                  stroke={strokeColor}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}

              {/* Rendered circles */}
              {(circles[currentPage] || []).map((c, idx) => (
                <ellipse
                  key={`circle-${idx}`}
                  cx={c.cx}
                  cy={c.cy}
                  rx={c.rx}
                  ry={c.ry}
                  fill="none"
                  stroke={c.stroke}
                  strokeWidth={c.strokeWidth}
                  className={selectedElement?.type === 'circle' && selectedElement?.index === idx ? 'stroke-blue-500' : ''}
                  style={{ pointerEvents: 'auto', cursor: activeTool === 'select' ? 'move' : 'default' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (activeTool === 'select') {
                      setSelectedElement({ type: 'circle', index: idx, pageNum: currentPage });
                    }
                  }}
                />
              ))}

              {/* Rendered lines */}
              {(lines[currentPage] || []).map((line, idx) => (
                <line
                  key={`line-${idx}`}
                  x1={line.x1}
                  y1={line.y1}
                  x2={line.x2}
                  y2={line.y2}
                  stroke={line.stroke}
                  strokeWidth={line.strokeWidth}
                  strokeLinecap="round"
                  style={{ pointerEvents: 'auto', cursor: activeTool === 'select' ? 'move' : 'default' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (activeTool === 'select') {
                      setSelectedElement({ type: 'line', index: idx, pageNum: currentPage });
                    }
                  }}
                />
              ))}

              {/* Rendered arrows */}
              {(arrows[currentPage] || []).map((arrow, idx) => {
                const angle = Math.atan2(arrow.y2 - arrow.y1, arrow.x2 - arrow.x1);
                const headLength = 12;
                return (
                  <g key={`arrow-${idx}`}>
                    <line
                      x1={arrow.x1}
                      y1={arrow.y1}
                      x2={arrow.x2}
                      y2={arrow.y2}
                      stroke={arrow.stroke}
                      strokeWidth={arrow.strokeWidth}
                      strokeLinecap="round"
                    />
                    <line
                      x1={arrow.x2}
                      y1={arrow.y2}
                      x2={arrow.x2 - headLength * Math.cos(angle - Math.PI/6)}
                      y2={arrow.y2 - headLength * Math.sin(angle - Math.PI/6)}
                      stroke={arrow.stroke}
                      strokeWidth={arrow.strokeWidth}
                      strokeLinecap="round"
                    />
                    <line
                      x1={arrow.x2}
                      y1={arrow.y2}
                      x2={arrow.x2 - headLength * Math.cos(angle + Math.PI/6)}
                      y2={arrow.y2 - headLength * Math.sin(angle + Math.PI/6)}
                      stroke={arrow.stroke}
                      strokeWidth={arrow.strokeWidth}
                      strokeLinecap="round"
                    />
                  </g>
                );
              })}

              {/* Rendered freehand */}
              {(freehand[currentPage] || []).map((drawing, idx) => (
                <polyline
                  key={`freehand-${idx}`}
                  points={drawing.points.map(p => `${p.x},${p.y}`).join(' ')}
                  fill="none"
                  stroke={drawing.stroke}
                  strokeWidth={drawing.strokeWidth}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ))}
            </svg>

            {/* Current drawing rectangle */}
            {currentShape?.type === 'rect' && (
              <div
                className="absolute border-2 border-blue-500 border-dashed pointer-events-none"
                style={{
                  left: currentShape.x * scale,
                  top: currentShape.y * scale,
                  width: currentShape.width * scale,
                  height: currentShape.height * scale,
                  backgroundColor: `${currentShape.color}80`
                }}
              />
            )}

            {/* Render rectangles */}
            {(rectangles[currentPage] || []).map((rect, idx) => {
              const isSelected = selectedElement?.type === 'rect' && selectedElement?.index === idx;
              return (
                <div
                  key={`rect-${idx}`}
                  className={`absolute ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
                  style={{
                    left: rect.x * scale,
                    top: rect.y * scale,
                    width: rect.width * scale,
                    height: rect.height * scale,
                    backgroundColor: rect.color,
                    cursor: activeTool === 'select' ? 'move' : 'default'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (activeTool === 'select') {
                      setSelectedElement({ type: 'rect', index: idx, pageNum: currentPage });
                    }
                  }}
                >
                  {/* Resize handles when selected */}
                  {isSelected && activeTool === 'select' && (
                    <>
                      <div className="absolute -top-1 -left-1 w-3 h-3 bg-blue-500 cursor-nw-resize" style={{ pointerEvents: 'auto' }} />
                      <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-blue-500 cursor-n-resize" style={{ pointerEvents: 'auto' }} />
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 cursor-ne-resize" style={{ pointerEvents: 'auto' }} />
                      <div className="absolute top-1/2 -translate-y-1/2 -right-1 w-3 h-3 bg-blue-500 cursor-e-resize" style={{ pointerEvents: 'auto' }} />
                      <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-blue-500 cursor-se-resize" style={{ pointerEvents: 'auto' }} />
                      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-blue-500 cursor-s-resize" style={{ pointerEvents: 'auto' }} />
                      <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-blue-500 cursor-sw-resize" style={{ pointerEvents: 'auto' }} />
                      <div className="absolute top-1/2 -translate-y-1/2 -left-1 w-3 h-3 bg-blue-500 cursor-w-resize" style={{ pointerEvents: 'auto' }} />
                    </>
                  )}
                </div>
              );
            })}

            {/* Render PDF text blocks as clickable overlays */}
            {pdfData && pdfData.pages && pdfData.pages[currentPage - 1] && (
              pdfData.pages[currentPage - 1].text_blocks || []
            ).map((block) => {
              const pageEdits = pdfTextEdits[currentPage] || {};
              const isEdited = block.id in pageEdits;
              const displayText = isEdited ? pageEdits[block.id] : block.text;
              const isEditing = editingPdfBlock === block.id;
              const blockFontSize = Math.max(8, block.size || 12);
              
              return (
                <div
                  key={`pdfblock-${block.id}`}
                  className={`absolute cursor-pointer transition-all ${
                    isEditing ? 'z-20' : 'z-10'
                  } ${isEdited && !isEditing ? 'ring-1 ring-green-400/60' : ''}`}
                  style={{
                    left: block.x * scale,
                    top: block.y * scale,
                    width: (block.width + 4) * scale,
                    height: block.height * scale,
                    minWidth: 20,
                  }}
                  data-testid={`pdf-text-${block.id}`}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    setEditingPdfBlock(block.id);
                    setEditPdfText(displayText);
                    setSelectedElement(null);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (activeTool === 'select') {
                      setSelectedElement({ type: 'pdfText', id: block.id });
                    }
                  }}
                >
                  {isEditing ? (
                    <input
                      ref={editInputRef}
                      type="text"
                      value={editPdfText}
                      onChange={(e) => setEditPdfText(e.target.value)}
                      onBlur={() => {
                        if (editPdfText !== block.text) {
                          setPdfTextEdits(prev => ({
                            ...prev,
                            [currentPage]: { ...(prev[currentPage] || {}), [block.id]: editPdfText }
                          }));
                        }
                        setEditingPdfBlock(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          if (editPdfText !== block.text) {
                            setPdfTextEdits(prev => ({
                              ...prev,
                              [currentPage]: { ...(prev[currentPage] || {}), [block.id]: editPdfText }
                            }));
                          }
                          setEditingPdfBlock(null);
                        } else if (e.key === 'Escape') {
                          setEditingPdfBlock(null);
                        }
                      }}
                      className="w-full h-full border-2 border-blue-500 bg-white px-0.5 outline-none"
                      style={{
                        fontSize: blockFontSize * scale,
                        lineHeight: `${block.height * scale}px`,
                        fontFamily: block.font?.includes('Times') ? 'Times New Roman, serif' : block.font?.includes('Courier') ? 'Courier New, monospace' : 'Arial, Helvetica, sans-serif',
                        fontWeight: (block.flags & 16) ? 'bold' : 'normal',
                        fontStyle: (block.flags & 2) ? 'italic' : 'normal',
                        color: block.color !== undefined ? `#${(block.color || 0).toString(16).padStart(6, '0')}` : '#000000',
                      }}
                    />
                  ) : (
                    <div
                      className="w-full h-full hover:bg-blue-100/30 hover:outline hover:outline-1 hover:outline-blue-400/50 rounded-[1px]"
                      style={{ background: 'transparent' }}
                      title="Double-click to edit"
                    />
                  )}
                </div>
              );
            })}

            {/* Render text boxes */}
            {(textBoxes[currentPage] || []).map((textBox, idx) => {
              const fs = textBox.font_size || textBox.fontSize || 14;
              const fontKey = textBox.font_family || textBox.fontFamily;
              const isSelected = selectedElement?.type === 'text' && selectedElement?.index === idx && selectedElement?.pageNum === currentPage;
              const isEditing = editingTextBox?.index === idx;
              return (
                <div
                  key={`text-${idx}`}
                  className={`absolute ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50/40' : ''}`}
                  data-testid={`canvas-text-${idx}`}
                  style={{
                    left: textBox.x * scale,
                    top: (textBox.y - fs) * scale,
                    fontSize: fs * scale,
                    color: textBox.color || '#000000',
                    fontFamily: cssFontFamilyFor(fontKey),
                    fontWeight: textBox.bold ? 'bold' : 'normal',
                    cursor: activeTool === 'select' ? 'move' : 'default',
                    whiteSpace: 'nowrap',
                    padding: '2px 4px',
                    touchAction: activeTool === 'select' ? 'none' : 'auto',
                    userSelect: 'none',
                  }}
                  onPointerDown={(e) => {
                    if (activeTool !== 'select' || isEditing) return;
                    e.stopPropagation();
                    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
                    setSelectedElement({ type: 'text', index: idx, pageNum: currentPage });
                    textDragRef.current = {
                      idx,
                      startClientX: e.clientX,
                      startClientY: e.clientY,
                      startX: textBox.x,
                      startY: textBox.y,
                      moved: false,
                    };
                  }}
                  onPointerMove={(e) => {
                    const drag = textDragRef.current;
                    if (!drag || drag.idx !== idx) return;
                    const dx = (e.clientX - drag.startClientX) / scale;
                    const dy = (e.clientY - drag.startClientY) / scale;
                    if (!drag.moved && Math.abs(dx) + Math.abs(dy) > 1.5) drag.moved = true;
                    if (drag.moved) {
                      e.stopPropagation();
                      updateElementPosition(
                        { type: 'text', index: idx, pageNum: currentPage },
                        drag.startX + dx,
                        drag.startY + dy
                      );
                    }
                  }}
                  onPointerUp={(e) => {
                    const drag = textDragRef.current;
                    textDragRef.current = null;
                    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
                    if (drag && drag.moved) {
                      e.stopPropagation();
                      setTimeout(saveToHistory, 50);
                    }
                  }}
                  onPointerCancel={() => { textDragRef.current = null; }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (activeTool === 'select') {
                      setSelectedElement({ type: 'text', index: idx, pageNum: currentPage });
                    }
                  }}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    setEditingTextBox({ index: idx, text: textBox.text });
                  }}
                >
                  {isEditing ? (
                    <input
                      type="text"
                      value={editingTextBox.text}
                      onChange={(e) => setEditingTextBox({ ...editingTextBox, text: e.target.value })}
                      onBlur={() => {
                        updateTextBox(idx, editingTextBox.text);
                        setEditingTextBox(null);
                        saveToHistory();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          updateTextBox(idx, editingTextBox.text);
                          setEditingTextBox(null);
                          saveToHistory();
                        }
                      }}
                      autoFocus
                      className="bg-white border border-blue-500 px-1 outline-none"
                      style={{ fontSize: 'inherit', color: 'inherit', fontFamily: 'inherit', fontWeight: 'inherit' }}
                    />
                  ) : (
                    textBox.text
                  )}
                  {/* Floating action chips while selected (mobile-friendly tap targets) */}
                  {isSelected && !isEditing && (
                    <div
                      className="absolute -top-[72px] left-0 flex flex-col gap-1 z-30"
                      style={{ touchAction: 'auto' }}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Row 1 — type controls: Match Nearby, Font, Size −/+, Bold */}
                      <div className="flex items-center gap-1 bg-white rounded-md shadow-md border border-slate-200 px-1.5 py-1 text-xs">
                        <button
                          type="button"
                          className="px-1.5 py-0.5 rounded bg-amber-100 hover:bg-amber-200 text-amber-900 font-medium text-[11px] whitespace-nowrap"
                          onClick={(e) => {
                            e.stopPropagation();
                            matchNearbyTextStyle(idx);
                          }}
                          data-testid={`canvas-text-match-${idx}`}
                          title="Copy font / size / colour from the closest original text on the page"
                        >
                          ✨ Match
                        </button>
                        <span className="text-slate-300">|</span>
                        <select
                          value={textBox.font_family || textBox.fontFamily || 'helv'}
                          onChange={(e) => patchTextBoxProps(idx, { font_family: e.target.value })}
                          className="text-[11px] border border-slate-200 rounded px-1 py-0.5 max-w-[110px] focus:outline-none focus:ring-1 focus:ring-blue-400"
                          data-testid={`canvas-text-font-${idx}`}
                          title="Font family"
                        >
                          {(availableFonts.length > 0 ? availableFonts : [
                            { key: 'helv', label: 'Helvetica' },
                            { key: 'hebo', label: 'Helvetica Bold' },
                            { key: 'tiro', label: 'Times Roman' },
                            { key: 'tibo', label: 'Times Bold' },
                            { key: 'cour', label: 'Courier' },
                          ]).map(f => (
                            <option key={f.key} value={f.key}>{f.label || f.key}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="w-6 h-6 rounded hover:bg-slate-100 text-slate-700 font-bold leading-none"
                          onClick={(e) => {
                            e.stopPropagation();
                            const cur = textBox.font_size || textBox.fontSize || 12;
                            patchTextBoxProps(idx, { font_size: Math.max(6, +(cur - 1).toFixed(1)) });
                          }}
                          data-testid={`canvas-text-size-down-${idx}`}
                          title="Decrease font size"
                        >−</button>
                        <span className="text-[11px] text-slate-700 min-w-[28px] text-center tabular-nums">
                          {(textBox.font_size || textBox.fontSize || 12).toFixed(0)}pt
                        </span>
                        <button
                          type="button"
                          className="w-6 h-6 rounded hover:bg-slate-100 text-slate-700 font-bold leading-none"
                          onClick={(e) => {
                            e.stopPropagation();
                            const cur = textBox.font_size || textBox.fontSize || 12;
                            patchTextBoxProps(idx, { font_size: Math.min(96, +(cur + 1).toFixed(1)) });
                          }}
                          data-testid={`canvas-text-size-up-${idx}`}
                          title="Increase font size"
                        >+</button>
                        <button
                          type="button"
                          className={`w-6 h-6 rounded font-bold text-[11px] ${textBox.bold ? 'bg-slate-800 text-white' : 'hover:bg-slate-100 text-slate-700'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            patchTextBoxProps(idx, { bold: !textBox.bold });
                          }}
                          data-testid={`canvas-text-bold-${idx}`}
                          title="Toggle bold"
                        >B</button>
                        <input
                          type="color"
                          value={textBox.color || '#000000'}
                          onChange={(e) => patchTextBoxProps(idx, { color: e.target.value })}
                          className="w-6 h-6 rounded cursor-pointer border border-slate-200"
                          data-testid={`canvas-text-color-${idx}`}
                          title="Text colour"
                        />
                      </div>
                      {/* Row 2 — actions: Edit / Delete */}
                      <div className="flex items-center gap-1 bg-white rounded-md shadow-md border border-slate-200 px-1.5 py-1 text-xs self-start">
                        <button
                          type="button"
                          className="px-1.5 py-0.5 rounded hover:bg-slate-100 text-indigo-600 font-medium"
                          data-testid={`canvas-text-edit-${idx}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingTextBox({ index: idx, text: textBox.text });
                          }}
                        >
                          ✎ Edit
                        </button>
                        <span className="text-slate-300">|</span>
                        <button
                          type="button"
                          className="px-1.5 py-0.5 rounded hover:bg-red-50 text-red-600 font-medium"
                          data-testid={`canvas-text-delete-${idx}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSelectedElement();
                          }}
                        >
                          ✕ Delete
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Drag-handle visual cue at bottom-right when selected (helps discover the drag affordance) */}
                  {isSelected && !isEditing && (
                    <div
                      className="absolute -bottom-2.5 -right-2.5 w-6 h-6 rounded-full bg-blue-500 text-white shadow-md flex items-center justify-center text-[10px] font-bold pointer-events-none select-none"
                      title="Drag to move"
                      data-testid={`canvas-text-grip-${idx}`}
                    >
                      ⠿
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Panel — fixed sidebar on desktop, collapsible on mobile.
            Default: hidden on mobile so the canvas takes full screen height.
            User can tap the floating "?" button to expand. */}
        <div className={`${showMobileHelp ? 'block' : 'hidden'} sm:block w-full sm:w-64 max-h-[55vh] sm:max-h-none bg-white border-t sm:border-t-0 sm:border-l p-3 sm:p-4 overflow-y-auto flex-shrink-0`}>
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h3 className="font-semibold text-sm sm:text-base">Canvas Editor</h3>
            <button
              onClick={() => setShowMobileHelp(false)}
              className="sm:hidden p-1 rounded hover:bg-slate-100"
              data-testid="mobile-help-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="text-sm text-slate-600 bg-blue-50 p-3 rounded-lg">
              <p className="font-medium mb-2">Tools:</p>
              <ul className="list-none space-y-1 text-xs">
                <li><strong>Rectangle</strong>: Cover text</li>
                <li><strong>Circle</strong>: Highlight areas</li>
                <li><strong>Line/Arrow</strong>: Point to items</li>
                <li><strong>Freehand</strong>: Draw freely</li>
                <li><strong>Text</strong>: Add new text</li>
              </ul>
            </div>
            
            <div className="text-sm text-slate-600 bg-green-50 p-3 rounded-lg">
              <p className="font-medium mb-2">Shortcuts:</p>
              <ul className="list-none space-y-1 text-xs">
                <li><kbd className="px-1 bg-slate-200 rounded">Ctrl+Z</kbd> Undo · <kbd className="px-1 bg-slate-200 rounded">Ctrl+Y</kbd> Redo</li>
                <li><kbd className="px-1 bg-slate-200 rounded">Delete</kbd> Remove selected</li>
                <li><kbd className="px-1 bg-indigo-200 rounded font-bold">Q</kbd> <strong>Quick Edit</strong> · <kbd className="px-1 bg-amber-200 rounded">M</kbd> Magic Erase · <kbd className="px-1 bg-slate-200 rounded">R</kbd> Rectangle · <kbd className="px-1 bg-slate-200 rounded">T</kbd> Text</li>
                <li><kbd className="px-1 bg-slate-200 rounded">V</kbd> Select · <kbd className="px-1 bg-slate-200 rounded">I</kbd> Pipette · <kbd className="px-1 bg-slate-200 rounded">D</kbd> Draw</li>
              </ul>
            </div>

            {(activeTool === 'line' || activeTool === 'arrow' || activeTool === 'circle' || activeTool === 'freehand') && (
              <div className="space-y-2">
                <Label>Stroke Width: {strokeWidth}px</Label>
                <Slider
                  value={[strokeWidth]}
                  onValueChange={([val]) => setStrokeWidth(val)}
                  min={1}
                  max={10}
                  step={1}
                />
              </div>
            )}

            {activeTool === 'eraser' && (
              <div className="space-y-2 bg-blue-50 p-3 rounded-lg" data-testid="eraser-controls">
                <div className="flex items-center justify-between">
                  <Label className="font-medium text-blue-900">Eraser Brush: {eraserBrushSize}px</Label>
                  <span className="text-[10px] text-blue-700 uppercase tracking-wide">E</span>
                </div>
                <Slider
                  value={[eraserBrushSize]}
                  onValueChange={([val]) => setEraserBrushSize(val)}
                  min={8}
                  max={80}
                  step={2}
                  data-testid="eraser-brush-slider"
                />
                <label className="flex items-center gap-2 text-xs text-blue-900 cursor-pointer mt-2">
                  <input
                    type="checkbox"
                    checked={smartEraser}
                    onChange={(e) => { setSmartEraser(e.target.checked); setHoveredTextBbox(null); }}
                    className="rounded text-blue-600 focus:ring-blue-500"
                    data-testid="smart-eraser-toggle"
                  />
                  <span><strong>Smart mode</strong> — tap a word to erase just that word</span>
                </label>
                <p className="text-xs text-blue-800 leading-tight">
                  {smartEraser
                    ? 'Hover near a word → it highlights → tap once to erase only that word.'
                    : 'Paint over text to erase — works with finger on mobile.'}
                  {' '}Uses page colour ({sampledPageColor || '#FFFFFF'}).
                </p>
              </div>
            )}
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-2">Pending Changes:</p>
              <ul className="text-xs text-slate-500 space-y-1">
                <li>Rectangles: {Object.values(rectangles).reduce((sum, arr) => sum + arr.length, 0)}</li>
                <li>Circles: {Object.values(circles).reduce((sum, arr) => sum + arr.length, 0)}</li>
                <li>Lines: {Object.values(lines).reduce((sum, arr) => sum + arr.length, 0)}</li>
                <li>Arrows: {Object.values(arrows).reduce((sum, arr) => sum + arr.length, 0)}</li>
                <li>Drawings: {Object.values(freehand).reduce((sum, arr) => sum + arr.length, 0)}</li>
                <li>Text Boxes: {Object.values(textBoxes).reduce((sum, arr) => sum + arr.length, 0)}</li>
                <li className={Object.keys(pdfTextEdits).length > 0 ? 'text-green-700 font-medium' : ''}>
                  Text Edits: {Object.values(pdfTextEdits).reduce((sum, edits) => sum + Object.keys(edits).length, 0)}
                </li>
              </ul>
            </div>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-2">History:</p>
              <p className="text-xs text-slate-500">
                Step {historyIndex + 1} of {history.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Help / Properties FAB — visible only on mobile when properties panel is hidden */}
      {!showMobileHelp && (
        <button
          onClick={() => setShowMobileHelp(true)}
          className="sm:hidden fixed bottom-4 right-4 z-50 w-12 h-12 rounded-full bg-blue-600 text-white shadow-lg flex items-center justify-center text-xl font-bold active:scale-95"
          data-testid="mobile-help-fab"
          aria-label="Show editor info"
        >
          ?
        </button>
      )}

      {/* Add Text Dialog */}
      <Dialog open={addTextDialog} onOpenChange={setAddTextDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Text</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Text Content</Label>
              <Textarea
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                placeholder="Enter text..."
                rows={3}
                data-testid="new-text-input"
              />
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <Label>Font Size: {fontSize}px</Label>
                <Slider
                  value={[fontSize]}
                  onValueChange={([val]) => setFontSize(val)}
                  min={8}
                  max={48}
                  step={1}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Color</Label>
                <Input 
                  type="color" 
                  value={textColor} 
                  onChange={(e) => setTextColor(e.target.value)}
                  className="w-16 h-10 mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddTextDialog(false)}>Cancel</Button>
            <Button onClick={handleAddText} className="bg-blue-600 hover:bg-blue-700" data-testid="add-text-confirm">
              Add Text
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save to Library Dialog */}
      <Dialog open={saveToLibraryDialog} onOpenChange={setSaveToLibraryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save to Document Library?</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Your PDF has been saved. Would you like to also save it to your Document Library for easy access later?
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSaveToLibraryDialog(false); onSave?.(); }}>No, Thanks</Button>
            <Button onClick={handleSaveToLibraryFromDialog} className="bg-purple-600 hover:bg-purple-700">
              <FolderOpen className="w-4 h-4 mr-2" />
              Yes, Save to Library
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Quick Edit Dialog — appears after user drags a Quick Edit rectangle */}
      <Dialog open={quickEditDialog} onOpenChange={(open) => { if (!open) setQuickEditDialog(false); }}>
        <DialogContent className="max-w-md sm:max-w-lg" data-testid="quick-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Sparkles className="w-4 h-4 text-indigo-600" />
              Quick Edit Text
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {quickEditAnalyzing ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 rounded p-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Analysing region — paper colour, font, size…
              </div>
            ) : (
              <div className="text-xs text-slate-500 bg-indigo-50 rounded p-2 space-y-1">
                <div>
                  Detected — paper <span className="font-mono">{quickEditPaperColor}</span> ·
                  size <span className="font-semibold">{quickEditFont.size.toFixed(1)}pt</span> ·
                  colour <span className="font-mono">{quickEditFont.color}</span>
                  {quickEditFont.bold && <span className="ml-1 px-1 bg-indigo-200 rounded font-bold">bold</span>}
                </div>
                {quickEditDetection && (
                  <div className="text-[11px]">
                    Font family guess: <span className="font-semibold capitalize">{quickEditDetection.family}</span>
                    <span className={`ml-1 px-1.5 rounded text-[10px] uppercase tracking-wide ${
                      quickEditDetection.confidence === 'high' ? 'bg-green-200 text-green-900' :
                      quickEditDetection.confidence === 'medium' ? 'bg-amber-200 text-amber-900' :
                      'bg-red-200 text-red-900'
                    }`}>{quickEditDetection.confidence}</span>
                    {quickEditDetection.confidence !== 'high' && (
                      <span className="ml-2 text-slate-600 italic">— please confirm the family below</span>
                    )}
                  </div>
                )}
              </div>
            )}
            {/* Family quick-pick chips — easier than scrolling a long font dropdown.
                Lets the user override low-confidence detection in one tap. */}
            {!quickEditAnalyzing && (
              <div className="flex items-center gap-1 flex-wrap" data-testid="quick-edit-family-chips">
                <span className="text-[11px] text-slate-500">Family:</span>
                {[
                  { key: 'sans',  label: 'Sans',  regular: 'lib-sans',  bold: 'lib-sans-b' },
                  { key: 'serif', label: 'Serif', regular: 'lib-serif', bold: 'lib-serif-b' },
                  { key: 'mono',  label: 'Mono',  regular: 'lib-mono',  bold: 'lib-mono-b' },
                ].map(fam => {
                  const target = quickEditFont.bold ? fam.bold : fam.regular;
                  const isActive = quickEditFont.family === target ||
                                   (fam.key === 'sans' && (quickEditFont.family === 'helv' || quickEditFont.family === 'hebo')) ||
                                   (fam.key === 'serif' && (quickEditFont.family === 'tiro' || quickEditFont.family === 'tibo')) ||
                                   (fam.key === 'mono' && (quickEditFont.family === 'cour' || quickEditFont.family === 'cobo'));
                  return (
                    <button
                      key={fam.key}
                      type="button"
                      onClick={() => setQuickEditFont(prev => ({ ...prev, family: prev.bold ? fam.bold : fam.regular }))}
                      className={`px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                        isActive ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                      }`}
                      style={{
                        fontFamily: fam.key === 'serif' ? '"Times New Roman", serif' :
                                    fam.key === 'mono'  ? '"Courier New", monospace' :
                                    'Helvetica, Arial, sans-serif',
                      }}
                      data-testid={`quick-edit-family-${fam.key}`}
                    >
                      {fam.label}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => {
                    const newBold = !quickEditFont.bold;
                    // Toggle between regular/bold variants in the same family
                    const mapping = {
                      'lib-sans': 'lib-sans-b',  'lib-sans-b': 'lib-sans',
                      'lib-serif': 'lib-serif-b','lib-serif-b': 'lib-serif',
                      'lib-mono': 'lib-mono-b',  'lib-mono-b': 'lib-mono',
                      'helv': 'hebo', 'hebo': 'helv',
                      'tiro': 'tibo', 'tibo': 'tiro',
                      'cour': 'cobo', 'cobo': 'cour',
                    };
                    setQuickEditFont(prev => ({
                      ...prev,
                      bold: newBold,
                      family: mapping[prev.family] || prev.family,
                    }));
                  }}
                  className={`ml-1 px-2.5 py-1 rounded-full border text-xs font-bold transition-colors ${
                    quickEditFont.bold ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                  }`}
                  data-testid="quick-edit-bold-chip"
                >B</button>
              </div>
            )}
            {/* Live preview row — shows the replacement text rendered in the chosen font.
                User can compare against the original on the page before applying. */}
            {!quickEditAnalyzing && quickEditText.trim() && (
              <div className="bg-white border border-slate-200 rounded p-2" data-testid="quick-edit-live-preview">
                <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Live preview</div>
                <div
                  className="truncate"
                  style={{
                    fontSize: `${Math.min(28, Math.max(12, quickEditFont.size))}px`,
                    fontFamily: (() => {
                      const f = quickEditFont.family;
                      if (f.startsWith('lib-serif') || f === 'tiro' || f === 'tibo' || f === 'tiit' || f === 'tibi') return '"Times New Roman", "Liberation Serif", serif';
                      if (f.startsWith('lib-mono') || f === 'cour' || f === 'cobo' || f === 'coit' || f === 'cobi') return '"Courier New", "Liberation Mono", monospace';
                      return 'Helvetica, "Liberation Sans", Arial, sans-serif';
                    })(),
                    fontWeight: quickEditFont.bold ? 'bold' : 'normal',
                    color: quickEditFont.color,
                    background: quickEditPaperColor,
                    padding: '4px 6px',
                  }}
                >
                  {quickEditText}
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs">Replacement Text</Label>
              <Input
                autoFocus
                value={quickEditText}
                onChange={e => setQuickEditText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); applyQuickEdit(); } }}
                placeholder="Type the new text…"
                className="mt-1"
                data-testid="quick-edit-text-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Font</Label>
                <select
                  value={quickEditFont.family}
                  onChange={e => setQuickEditFont(prev => ({ ...prev, family: e.target.value }))}
                  className="mt-1 w-full text-sm border rounded-md px-2 py-1.5 bg-white"
                  data-testid="quick-edit-font-select"
                >
                  {(availableFonts.length > 0 ? availableFonts : [
                    { key: 'helv', label: 'Helvetica' },
                    { key: 'tiro', label: 'Times Roman' },
                  ]).map(f => (
                    <option key={f.key} value={f.key}>{f.label}</option>
                  ))}
                </select>
              </div>              <div>
                <Label className="text-xs">Size (pt)</Label>
                <Input
                  type="number"
                  step="0.5" min="6" max="200"
                  value={quickEditFont.size}
                  onChange={e => setQuickEditFont(prev => ({ ...prev, size: parseFloat(e.target.value) || 12 }))}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Text Colour</Label>
                <div className="flex items-center gap-1 mt-1">
                  <input type="color" value={quickEditFont.color}
                    onChange={e => setQuickEditFont(prev => ({ ...prev, color: e.target.value }))}
                    className="w-8 h-8 rounded border cursor-pointer" />
                  <Input value={quickEditFont.color}
                    onChange={e => setQuickEditFont(prev => ({ ...prev, color: e.target.value }))}
                    className="text-xs font-mono" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Paper Colour (cover)</Label>
                <div className="flex items-center gap-1 mt-1">
                  <input type="color" value={quickEditPaperColor}
                    onChange={e => setQuickEditPaperColor(e.target.value)}
                    className="w-8 h-8 rounded border cursor-pointer" />
                  <Input value={quickEditPaperColor}
                    onChange={e => setQuickEditPaperColor(e.target.value)}
                    className="text-xs font-mono" />
                </div>
              </div>
            </div>
            <label
              className="flex items-center gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 cursor-pointer select-none"
              data-testid="quick-edit-automatch-row"
            >
              <input
                type="checkbox"
                checked={autoMatchOnApply}
                onChange={(e) => toggleAutoMatchOnApply(e.target.checked)}
                className="rounded text-amber-600 focus:ring-amber-500"
                data-testid="quick-edit-automatch-toggle"
              />
              <span>
                <strong>✨ Auto-Match nearby style on Apply</strong> — best for filling many cells in forms
              </span>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setQuickEditDialog(false)} data-testid="quick-edit-cancel">Cancel</Button>
            <Button
              onClick={applyQuickEdit}
              disabled={!quickEditText.trim() || quickEditAnalyzing}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="quick-edit-apply"
            >
              <Sparkles className="w-4 h-4 mr-1" />
              Replace Text
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Side-by-side Compare Modal */}
      <Dialog open={compareOpen} onOpenChange={(open) => { if (!open) closeCompare(); }}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] p-0 flex flex-col" data-testid="compare-modal">
          <DialogHeader className="px-4 py-2 border-b flex-row items-center justify-between space-y-0">
            <DialogTitle className="text-base">Side-by-Side Compare — Original vs Edited</DialogTitle>
            <Button variant="ghost" size="sm" onClick={closeCompare} data-testid="compare-close-btn">
              <X className="w-4 h-4" />
            </Button>
          </DialogHeader>
          <div className="flex-1 grid grid-cols-2 gap-2 p-2 bg-slate-100 overflow-hidden min-h-0">
            <div className="flex flex-col bg-white rounded shadow-sm overflow-hidden">
              <div className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 border-b flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                Original
              </div>
              {compareOrigUrl ? (
                <iframe
                  title="Original"
                  src={`${compareOrigUrl}#toolbar=0`}
                  className="flex-1 w-full bg-white"
                  data-testid="compare-original-iframe"
                />
              ) : (
                <div className="flex-1 flex items-center justify-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
              )}
            </div>
            <div className="flex flex-col bg-white rounded shadow-sm overflow-hidden">
              <div className="px-3 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border-b flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                With Your Edits ({getTotalEdits()} change{getTotalEdits() !== 1 ? 's' : ''})
              </div>
              {comparePreviewUrl ? (
                <iframe
                  title="Edited"
                  src={`${comparePreviewUrl}#toolbar=0`}
                  className="flex-1 w-full bg-white"
                  data-testid="compare-edited-iframe"
                />
              ) : (
                <div className="flex-1 flex items-center justify-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Building preview…
                </div>
              )}
            </div>
          </div>
          <div className="px-4 py-2 border-t flex justify-end gap-2 bg-white">
            <Button variant="outline" onClick={closeCompare}>Keep Editing</Button>
            <Button
              onClick={() => { closeCompare(); handleSave(false); }}
              className="bg-green-600 hover:bg-green-700"
              data-testid="compare-save-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Looks Good — Save PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CanvasPDFEditor;
